import { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  getStats, getAdminStats, getAdminArticles, getPhotos, getPendingComments, 
  getPendingMemberships, getDeals, getCategories, getDestinations,
  deleteArticle, deletePhoto, moderateComment, uploadPhoto, generateArticle, reviseArticle,
  updateArticle, validateMembership, rejectMembership, createDeal, updateDeal, deleteDeal,
  getBanners, createBanner, updateBanner, deleteBanner,
  getProductReviews, getProductCategories, createProductReview, updateProductReview, deleteProductReview, generateProductReview,
  updateCategoryVisibility, getSiteSections, updateSectionVisibility, createSiteSection, deleteSiteSection,
  getCloudinaryStatus, getCloudinarySignature, saveCloudinaryPhoto, syncFromCloudinary, batchImportPhotos,
  getAlbums, createAlbum, getAlbum, updateAlbum, deleteAlbum, getAlbumPhotos, addPhotosToAlbum, removePhotosFromAlbum,
  getAIModels, suggestPhotoOrder, changePassword, rotatePhoto,
  getAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser, resetUserPassword, suspendUser, reactivateUser, getAdminRoles,
  getSettings, updateSettings
} from "../lib/api";
import { getThumbnailUrl, getMediumUrl, getCoverUrl, getFullSizeUrl } from "../lib/cloudinaryUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  LayoutDashboard, FileText, Image, MessageSquare, Users, Gift, Settings,
  Plus, Upload, Trash2, Eye, EyeOff, Edit, Check, X, Loader2, Sparkles, Calendar, 
  MapPin, Clock, AlertCircle, ChevronRight, Megaphone, Package, Star, Globe, Cloud, RefreshCw, FolderOpen,
  Edit3, ImagePlus, RotateCcw, RotateCw, FlipHorizontal, FlipVertical, Building2, Lock, FolderTree, GripVertical, ToggleLeft, ToggleRight, Shield, KeyRound, BookOpenText, BookMarked, Layers,
  Download, UserCog, Search, Ban, UserPlus, ZoomIn, ChevronLeft, Ship, UploadCloud, Pencil, Inbox, Mail, LogOut
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { BatchArticleGenerator } from "../components/BatchArticleGenerator";
import DealsManagementPage from "./DealsManagementPage";
import BannersManagementPage from "./BannersManagementPage";
import ProductReviewsAdminPage from "./ProductReviewsAdminPage";
import PartnerRequestsManagementPage from "./PartnerRequestsManagementPage";
import AdminDocumentsPage from "./AdminDocumentsPage";
import CategoriesManagementPage from "./CategoriesManagementPage";
import DestinationsManagementPage from "./DestinationsManagementPage";
import TripsManagementPage from "./TripsManagementPage";
import MediaImportPage from "./MediaImportPage";
import MediaDetailPage from "./MediaDetailPage";
import MembershipsPage from "./MembershipsPage";
import MembershipApplicationsPage from "./MembershipApplicationsPage";
import PaymentsListPage from "./PaymentsListPage";
import PrivilegesManagementPage from "./PrivilegesManagementPage";
import CommunicationsPage from "./CommunicationsPage";
import DiffusionPage from "./DiffusionPage";
import UpdatesPage from "./dashboard/UpdatesPage";
import BlueStudioPage from "./BlueStudioPage";
import AdminBluePublishingPage from "./AdminBluePublishingPage";
import SuperAdminPasskeysPage from "./SuperAdminPasskeysPage";
import VoyagesEtArticlesPage from "./VoyagesEtArticlesPage";
import MediaImportBatchesPage from "./MediaImportBatchesPage";
import MediaBatchDetailPage from "./MediaBatchDetailPage";
import MediaTrashPage from "./MediaTrashPage";
import PracticalSheetsListPage from "./PracticalSheetsListPage";
import PracticalSheetEditorPage from "./PracticalSheetEditorPage";
import NewsletterSubscribersPage from "./NewsletterSubscribersPage";
import IntelligencePage from "./IntelligencePage";
import { runWatermarkHistoricMigration, getWatermarkHistoricPending, getMemberships, getCloudinaryUsage, refreshCloudinaryUsage, getAdminAlerts, dismissAdminAlert, getSocialPendingCount } from "../lib/api";

// Status colors
const statusColors = {
  imported: "bg-gray-100 text-gray-700",
  analyzed: "bg-blue-100 text-blue-700",
  ai_draft: "bg-purple-100 text-purple-700",
  to_review: "bg-yellow-100 text-yellow-700",
  validated: "bg-green-100 text-green-700",
  published: "bg-green-500 text-white",
  archived: "bg-gray-200 text-gray-600",
};

const statusLabels = {
  imported: "Importé",
  analyzed: "Analysé",
  ai_draft: "Brouillon IA",
  to_review: "À relire",
  validated: "Validé",
  published: "Publié",
  archived: "Archivé",
};

// Dashboard Layout
function DashboardLayout({ children }) {
  const { user, isAuthenticated, loading, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/connexion", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/connexion" replace />;
  }

  const menuItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", exact: true },
    { path: "/dashboard/articles", icon: FileText, label: "Articles" },
    { path: "/dashboard/categories", icon: FolderTree, label: "Catégories" },
    { path: "/dashboard/destinations", icon: MapPin, label: "Destinations" },
    { path: "/dashboard/albums", icon: FolderOpen, label: "Albums" },
    { path: "/dashboard/photos", icon: Image, label: "Médiathèque" },
    { path: "/dashboard/media-import", icon: UploadCloud, label: "Ajouter des médias" },
    { path: "/dashboard/trips", icon: Ship, label: "Voyages" },
    { path: "/dashboard/voyages-articles", icon: BookOpenText, label: "Voyages & Articles" },
    { path: "/dashboard/medias/lots", icon: Layers, label: "Lots d'importation médias" },
    { path: "/dashboard/medias/corbeille", icon: Trash2, label: "Corbeille & analyse médias" },
    { path: "/dashboard/fiches-pratiques", icon: BookMarked, label: "Fiches pratiques" },
    { path: "/dashboard/reviews", icon: Package, label: "Tests Produits" },
    { path: "/dashboard/comments", icon: MessageSquare, label: "Commentaires" },
    {
      label: "Adhérents",
      icon: Users,
      adminOnly: true,
      children: [
        { path: "/dashboard/memberships", label: "Liste des adhérents" },
        { path: "/dashboard/membership-applications", label: "Demandes d'adhésion" },
        { path: "/dashboard/members", label: "Demandes espace membre" },
        { path: "/dashboard/adherents/paiements", label: "Paiements / cotisations" },
      ],
    },
    { path: "/dashboard/users", icon: UserCog, label: "Utilisateurs", adminOnly: true },
    { path: "/dashboard/privileges", icon: Sparkles, label: "Escales Privilèges", adminOnly: true },
    { path: "/dashboard/communications", icon: Mail, label: "Communications", adminOnly: true },
    { path: "/dashboard/newsletter", icon: Mail, label: "Newsletter", adminOnly: true },
    { path: "/dashboard/deals", icon: Gift, label: "Bons Plans" },
    { path: "/dashboard/partner-requests", icon: Building2, label: "Entreprises" },
    { path: "/dashboard/banners", icon: Megaphone, label: "Bannières" },
    { path: "/dashboard/diffusion", icon: Megaphone, label: "Diffusion", adminOnly: true },
    { path: "/dashboard/documents", icon: Lock, label: "Coffre-Fort" },
    { path: "/dashboard/settings", icon: Settings, label: "Paramètres" },
    { path: "/dashboard/parametres/securite", icon: KeyRound, label: "Sécurité — Passkeys", superAdminOnly: true },
    { path: "/dashboard/system/updates", icon: RefreshCw, label: "Mises à jour", adminOnly: true },
    { path: "/dashboard/blue-studio", icon: Sparkles, label: "Blue Studio", adminOnly: true },
    { path: "/dashboard/blue-publishing", icon: Sparkles, label: "Publication Blue", adminOnly: true },
    { path: "/dashboard/intelligence", icon: Search, label: "ITERCATUS Intelligence", adminOnly: true },
  ];

  return (
    <div className="min-h-screen bg-secondary" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-full md:w-64 shrink-0">
            <div className="bg-white border border-border rounded-lg overflow-hidden sticky top-8">
              <div className="p-6 border-b border-border">
                <p className="font-heading text-lg font-semibold text-primary">{user?.name}</p>
                <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
              </div>
              <nav className="py-2">
                {menuItems
                  .filter(item => !item.adminOnly || user?.role === 'admin' || user?.role === 'super_admin')
                  .filter(item => !item.superAdminOnly || user?.role === 'super_admin')
                  .map((item) => {
                    // Item avec sous-menu
                    if (item.children) {
                      const anyActive = item.children.some(c => location.pathname.startsWith(c.path));
                      return (
                        <div key={item.label} className="border-b border-border/40 last:border-0">
                          <div className={`sidebar-link cursor-default select-none ${anyActive ? "text-primary font-semibold" : ""}`}>
                            <item.icon className="h-5 w-5" />
                            {item.label}
                          </div>
                          <div className="pl-6">
                            {item.children.map(c => (
                              <Link
                                key={c.path}
                                to={c.path}
                                className={`sidebar-link text-sm ${location.pathname.startsWith(c.path) ? "active" : ""}`}
                                data-testid={`nav-${c.path.replace(/\//g, '-')}`}
                              >
                                <span className="w-1 h-1 rounded-full bg-current opacity-40 mr-2" />
                                {c.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    // Item simple
                    const isActive = item.exact
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar-link ${isActive ? "active" : ""}`}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.label}
                      </Link>
                    );
                  })}
              </nav>
              <div className="p-3 border-t border-border">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="sidebar-link w-full text-destructive hover:text-destructive"
                  data-testid="dashboard-logout-btn"
                >
                  <LogOut className="h-5 w-5" />
                  Déconnexion
                </button>
              </div>
            </div>
          </aside>

          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </div>
    </div>
  );
}

// Overview Page
function OverviewPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socialPending, setSocialPending] = useState(0);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [res, socialRes] = await Promise.all([
        getAdminStats(),
        getSocialPendingCount().catch(() => ({ data: { count: 0 } })),
      ]);
      setStats(res.data);
      setSocialPending(socialRes?.data?.count || 0);
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: "Brouillons IA", value: stats?.articles?.ai_drafts || 0, icon: Sparkles, color: "text-purple-600", link: "/dashboard/articles?status=ai_draft" },
    { label: "À relire", value: stats?.articles?.to_review || 0, icon: Clock, color: "text-yellow-600", link: "/dashboard/articles?status=to_review" },
    { label: "Commentaires", value: stats?.comments?.pending || 0, icon: MessageSquare, color: "text-blue-600", link: "/dashboard/comments" },
    { label: "Adhésions", value: stats?.members?.pending || 0, icon: Users, color: "text-green-600", link: "/dashboard/members" },
    ...(socialPending > 0
      ? [{
          label: `Diffusion — ${socialPending} ${socialPending > 1 ? "à valider" : "à valider"}`,
          value: socialPending,
          icon: Megaphone,
          color: "text-fuchsia-600",
          link: "/dashboard/diffusion",
          testid: "diffusion-pending-widget",
        }]
      : []),
  ];

  return (
    <div data-testid="overview-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-6">
        Tableau de bord
      </h1>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {loading ? (
          Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : (
          quickActions.map((action) => (
            <Link 
              key={action.label} 
              to={action.link}
              data-testid={action.testid}
              className="bg-white border border-border rounded-lg p-4 hover:border-accent/50 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <action.icon className={`h-5 w-5 ${action.color}`} />
                {action.value > 0 && (
                  <span className="w-6 h-6 bg-accent text-white text-xs rounded-full flex items-center justify-center">
                    {action.value}
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-primary">{action.value}</p>
              <p className="text-sm text-muted-foreground">{action.label}</p>
            </Link>
          ))
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-border rounded-lg p-6">
          <h3 className="font-heading text-lg font-semibold text-primary mb-4">Articles</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Publiés</span>
              <span className="font-medium">{stats?.articles?.published || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Brouillons</span>
              <span className="font-medium">{(stats?.articles?.ai_drafts || 0) + (stats?.articles?.to_review || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="font-medium">{stats?.articles?.total || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-6">
          <h3 className="font-heading text-lg font-semibold text-primary mb-4">Membres</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Actifs</span>
              <span className="font-medium">{stats?.members?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">En attente</span>
              <span className="font-medium">{stats?.members?.pending || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-6">
          <h3 className="font-heading text-lg font-semibold text-primary mb-4">Bons Plans</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Actifs</span>
              <span className="font-medium">{stats?.deals?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Réservés adhérents</span>
              <span className="font-medium">{stats?.deals?.members_only || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link 
          to="/dashboard/photos" 
          className="bg-white border border-border rounded-lg p-6 hover:border-accent transition-colors group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
              <Upload className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-primary">Ajouter des médias</h3>
              <p className="text-sm text-muted-foreground">Uploadez vos photos de voyage</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
          </div>
        </Link>

        {/* MEP P0: /batch-import masqué (outil interne non finalisé)
        <Link 
          to="/batch-import" 
          className="bg-white border border-border rounded-lg p-6 hover:border-accent transition-colors group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <ImagePlus className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-primary">Import par lot</h3>
              <p className="text-sm text-muted-foreground">Importez plusieurs photos et générez un brouillon</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
          </div>
        </Link>
        */}

        <Link 
          to="/import-draft" 
          className="bg-white border border-border rounded-lg p-6 hover:border-accent transition-colors group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-primary">Importer un brouillon</h3>
              <p className="text-sm text-muted-foreground">Intégrez un contenu préparé ailleurs</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
          </div>
        </Link>

        <Link 
          to="/dashboard/articles" 
          className="bg-white border border-border rounded-lg p-6 hover:border-accent transition-colors group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-primary">Générer un article</h3>
              <p className="text-sm text-muted-foreground">Créez du contenu avec l'IA</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
          </div>
        </Link>
      </div>
    </div>
  );
}

// Articles Page (simplified for space)
function ArticlesPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerator, setShowGenerator] = useState(false);
  const [showRevision, setShowRevision] = useState(null); // article to revise
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [articlesRes, categoriesRes, destinationsRes] = await Promise.all([
        getAdminArticles(),
        getCategories(),
        getDestinations()
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
      setDestinations(destinationsRes.data);
    } catch (error) {
      console.error("Error loading articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await deleteArticle(id);
      toast.success("Article supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleStatusChange = async (article, newStatus) => {
    try {
      await updateArticle(article.id, { status: newStatus });
      toast.success(`Article ${statusLabels[newStatus].toLowerCase()}`);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const filteredArticles = statusFilter === "all" 
    ? articles 
    : articles.filter(a => a.status === statusFilter);

  return (
    <div data-testid="articles-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">Articles</h1>
        <Button className="bg-accent hover:bg-accent/90 gap-2" onClick={() => setShowGenerator(true)}>
          <Sparkles className="h-4 w-4" />
          Générer avec l'IA
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "ai_draft", "to_review", "validated", "published"].map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(status)}
            className={statusFilter === status ? "bg-primary" : ""}
          >
            {status === "all" ? "Tous" : statusLabels[status]}
          </Button>
        ))}
      </div>

      {showGenerator && (
        <ArticleGenerator 
          categories={categories}
          destinations={destinations}
          onClose={() => setShowGenerator(false)}
          onSuccess={() => { setShowGenerator(false); loadData(); }}
        />
      )}

      {showRevision && (
        <ArticleRevisionModal
          article={showRevision}
          onClose={() => setShowRevision(null)}
          onSuccess={() => { setShowRevision(null); loadData(); }}
        />
      )}

      {loading ? (
        <div className="space-y-4">
          {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : filteredArticles.length > 0 ? (
        <div className="space-y-4">
          {filteredArticles.map((article) => {
            const thumbUrl = article.photos?.[0]?.cloudinary_url || 
              (article.photos?.[0]?.image_data ? `data:image/jpeg;base64,${article.photos[0].image_data}` : null);
            
            return (
            <div key={article.id} className="bg-white border border-border rounded-lg p-4 md:p-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Thumbnail */}
                <div className="w-full md:w-32 h-24 shrink-0 bg-muted rounded overflow-hidden">
                  {thumbUrl ? (
                    <img 
                      src={thumbUrl}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-primary line-clamp-1">{article.title}</h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[article.status]}`}>
                          {statusLabels[article.status]}
                        </span>
                        {article.category_name && <span>{article.category_name}</span>}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(article.created_at), "d MMM yyyy", { locale: fr })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Image className="h-3 w-3" />
                          {article.photos?.length || 0} photos
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => window.open(`/article/${article.id}`, "_blank")}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {/* Edit button - navigate to article editor */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => navigate(`/article/${article.id}/edit`)} 
                        title="Modifier l'article"
                        className="text-primary hover:text-primary/80"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      {/* Revision button - always available */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setShowRevision(article)} 
                        title="Réviser / Ajouter des photos"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      
                      {article.status === "ai_draft" && (
                        <Button variant="ghost" size="icon" onClick={() => handleStatusChange(article, "to_review")} title="Marquer à relire">
                          <Clock className="h-4 w-4 text-yellow-600" />
                        </Button>
                      )}
                      
                      {article.status === "to_review" && (
                        <Button variant="ghost" size="icon" onClick={() => handleStatusChange(article, "validated")} title="Valider">
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      
                      {article.status === "validated" && (
                        <Button variant="ghost" size="icon" onClick={() => handleStatusChange(article, "published")} title="Publier">
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      
                      {article.status === "published" && (
                        <Button variant="ghost" size="icon" onClick={() => handleStatusChange(article, "archived")} title="Archiver">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(article.id)} className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )})}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-border rounded-lg">
          <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucun article</h3>
          <p className="text-muted-foreground mb-4">Créez votre premier article avec l'IA.</p>
          <Button className="bg-accent hover:bg-accent/90 gap-2" onClick={() => setShowGenerator(true)}>
            <Sparkles className="h-4 w-4" />
            Générer un article
          </Button>
        </div>
      )}
    </div>
  );
}

// Article Generator with enhanced options
function ArticleGenerator({ categories, destinations, onClose, onSuccess }) {
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [form, setForm] = useState({
    category_id: "",
    destination_id: "",
    content_type: "article",
    location: "",
    venue_name: "",
    company: "",
    dates: "",
    ambiance: "",
    highlights: "",
    lowlights: "",
    practical_info: "",
    editorial_angle: "",
    tone: "professionnel et inspirant",
    keywords: "",
    must_mention: "",
    length: "long",  // Par défaut "long" pour articles magazine (1800+ mots)
    seo_optimized: true
  });

  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    try {
      const res = await getPhotos({ status: "ready" });
      setPhotos(res.data);
    } catch (error) {
      console.error("Error loading photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const togglePhoto = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]
    );
  };

  const handleGenerate = async () => {
    if (selectedPhotos.length === 0) {
      toast.error("Sélectionnez au moins une photo");
      return;
    }
    if (!form.category_id) {
      toast.error("Sélectionnez une catégorie");
      return;
    }

    setGenerating(true);
    try {
      const requestData = {
        photo_ids: selectedPhotos,
        category_id: form.category_id,
        destination_id: form.destination_id || undefined,
        content_type: form.content_type,
        location: form.location || undefined,
        venue_name: form.venue_name || undefined,
        company: form.company || undefined,
        dates: form.dates || undefined,
        ambiance: form.ambiance || undefined,
        highlights: form.highlights || undefined,
        lowlights: form.lowlights || undefined,
        practical_info: form.practical_info || undefined,
        editorial_angle: form.editorial_angle || undefined,
        tone: form.tone,
        keywords: form.keywords ? form.keywords.split(",").map(k => k.trim()) : undefined,
        must_mention: form.must_mention || undefined,
        length: form.length,
        seo_optimized: form.seo_optimized
      };

      await generateArticle(requestData);
      toast.success("Article généré avec succès !");
      onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la génération";
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold text-primary">Génération IA avancée</h2>
        <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Photos & Basic Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Catégorie *</label>
            {categories.length === 0 ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="text-amber-800 mb-2">
                  Aucune catégorie disponible.
                </p>
                <a href="/dashboard/categories" className="text-accent hover:underline font-medium">
                  → Créer une catégorie
                </a>
              </div>
            ) : (
              <Select value={form.category_id} onValueChange={(v) => setForm(prev => ({ ...prev, category_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">Destination</label>
            {destinations.length === 0 ? (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="text-blue-800 mb-2">
                  Aucune destination disponible.
                </p>
                <a href="/dashboard/destinations" className="text-accent hover:underline font-medium">
                  → Créer une destination
                </a>
              </div>
            ) : (
              <Select value={form.destination_id} onValueChange={(v) => setForm(prev => ({ ...prev, destination_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                <SelectContent>
                  {destinations.map(dest => (
                    <SelectItem key={dest.id} value={dest.id}>{dest.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">Type de contenu</label>
            <Select value={form.content_type} onValueChange={(v) => setForm(prev => ({ ...prev, content_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="article">Article complet</SelectItem>
                <SelectItem value="cruise_sheet">Fiche croisière</SelectItem>
                <SelectItem value="hotel_sheet">Fiche hôtel</SelectItem>
                <SelectItem value="restaurant_sheet">Fiche restaurant</SelectItem>
                <SelectItem value="summary">Résumé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Photos ({selectedPhotos.length} sélectionnée{selectedPhotos.length > 1 ? 's' : ''})
            </label>
            {loading ? (
              <Skeleton className="h-32" />
            ) : photos.length > 0 ? (
              <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-1">
                {photos.map((photo) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => togglePhoto(photo.id)}
                    className={`relative aspect-square overflow-hidden rounded border-2 ${
                      selectedPhotos.includes(photo.id) ? "border-accent" : "border-transparent"
                    }`}
                  >
                    <img src={`data:image/jpeg;base64,${photo.image_data}`} alt="" className="w-full h-full object-cover" />
                    {selectedPhotos.includes(photo.id) && (
                      <div className="absolute inset-0 bg-accent/20 flex items-center justify-center">
                        <Check className="h-6 w-6 text-accent" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Uploadez des photos d'abord</p>
            )}
          </div>
        </div>

        {/* Right Column - Detailed Instructions */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Lieu</label>
              <Input
                value={form.location}
                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Ex: Barcelone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Établissement</label>
              <Input
                value={form.venue_name}
                onChange={(e) => setForm(prev => ({ ...prev, venue_name: e.target.value }))}
                placeholder="Nom hôtel/restaurant/bateau"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Compagnie</label>
              <Input
                value={form.company}
                onChange={(e) => setForm(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Ex: MSC Croisières"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Dates</label>
              <Input
                value={form.dates}
                onChange={(e) => setForm(prev => ({ ...prev, dates: e.target.value }))}
                placeholder="Ex: Juin 2024"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">Points forts à mentionner</label>
            <Input
              value={form.highlights}
              onChange={(e) => setForm(prev => ({ ...prev, highlights: e.target.value }))}
              placeholder="Ex: Vue exceptionnelle, service impeccable..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">Points faibles</label>
            <Input
              value={form.lowlights}
              onChange={(e) => setForm(prev => ({ ...prev, lowlights: e.target.value }))}
              placeholder="Ex: Wifi limité..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-2">Angle éditorial</label>
            <Textarea
              value={form.editorial_angle}
              onChange={(e) => setForm(prev => ({ ...prev, editorial_angle: e.target.value }))}
              placeholder="Ex: Focus sur l'expérience gastronomique..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Ton</label>
              <Select value={form.tone} onValueChange={(v) => setForm(prev => ({ ...prev, tone: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professionnel et inspirant">Professionnel</SelectItem>
                  <SelectItem value="décontracté et enthousiaste">Décontracté</SelectItem>
                  <SelectItem value="luxueux et raffiné">Luxueux</SelectItem>
                  <SelectItem value="aventurier et passionné">Aventurier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Longueur</label>
              <Select value={form.length} onValueChange={(v) => setForm(prev => ({ ...prev, length: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Court (400-600 mots)</SelectItem>
                  <SelectItem value="medium">Moyen (1000-1500 mots)</SelectItem>
                  <SelectItem value="long">Long / Magazine (1800+ mots)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.seo_optimized} onCheckedChange={(v) => setForm(prev => ({ ...prev, seo_optimized: v }))} />
            <span className="text-sm">Optimiser pour le SEO</span>
          </div>
        </div>
      </div>

      <Button
        className="w-full mt-6 bg-accent hover:bg-accent/90 gap-2"
        onClick={handleGenerate}
        disabled={generating || selectedPhotos.length === 0 || !form.category_id}
      >
        {generating ? (
          <><Loader2 className="h-4 w-4 animate-spin" />Génération en cours...</>
        ) : (
          <><Sparkles className="h-4 w-4" />Générer le contenu</>
        )}
      </Button>
    </div>
  );
}

// Albums Page - Organize photos by trip/collection
function AlbumsPage() {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [categories, setCategories] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [showAddPhotos, setShowAddPhotos] = useState(false);
  const [availablePhotos, setAvailablePhotos] = useState([]);
  const [selectedForAdd, setSelectedForAdd] = useState([]);
  
  // New states for enhanced features
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, errors: [] });
  const [draggedPhotoId, setDraggedPhotoId] = useState(null);
  const [coverPhotoId, setCoverPhotoId] = useState(null);
  const [photoOrder, setPhotoOrder] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [albumsRes, catsRes, destsRes] = await Promise.all([
        getAlbums(),
        getCategories(),
        getDestinations()
      ]);
      setAlbums(albumsRes.data);
      setCategories(catsRes.data);
      setDestinations(destsRes.data);
    } catch (error) {
      console.error("Error loading albums:", error);
      toast.error("Erreur lors du chargement des albums");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = async (data) => {
    try {
      const result = await createAlbum(data);
      toast.success("Album créé avec succès");
      setShowCreate(false);
      loadData();
      // Open the new album directly
      openAlbum(result.data);
    } catch (error) {
      toast.error("Erreur lors de la création de l'album");
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm("Supprimer cet album ? Les photos ne seront pas supprimées.")) return;
    try {
      await deleteAlbum(albumId);
      toast.success("Album supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openAlbum = async (album) => {
    setSelectedAlbum(album);
    setHasUnsavedChanges(false);
    try {
      const [albumRes, photosRes] = await Promise.all([
        getAlbum(album.id),
        getAlbumPhotos(album.id)
      ]);
      const freshAlbum = albumRes.data;
      setSelectedAlbum(freshAlbum);
      setAlbumPhotos(photosRes.data);
      setCoverPhotoId(freshAlbum.cover_photo_id || null);
      
      // Initialize photo order from album or from photos array
      if (freshAlbum.photo_order && freshAlbum.photo_order.length > 0) {
        setPhotoOrder(freshAlbum.photo_order);
      } else {
        setPhotoOrder(photosRes.data.map(p => p.id));
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des photos");
    }
  };

  const openAddPhotos = async () => {
    try {
      const res = await getPhotos();
      // Filter out photos already in this album
      const available = res.data.filter(p => p.album_id !== selectedAlbum.id);
      setAvailablePhotos(available);
      setSelectedForAdd([]);
      setShowAddPhotos(true);
    } catch (error) {
      toast.error("Erreur lors du chargement de la médiathèque");
    }
  };

  const handleAddPhotos = async () => {
    if (selectedForAdd.length === 0) return;
    try {
      await addPhotosToAlbum(selectedAlbum.id, selectedForAdd);
      toast.success(`${selectedForAdd.length} photo(s) ajoutée(s)`);
      setShowAddPhotos(false);
      // Update local state
      const newPhotoOrder = [...photoOrder, ...selectedForAdd];
      setPhotoOrder(newPhotoOrder);
      openAlbum(selectedAlbum);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout des photos");
    }
  };

  const handleRemovePhoto = async (photoId) => {
    try {
      await removePhotosFromAlbum(selectedAlbum.id, [photoId]);
      toast.success("Photo retirée de l'album");
      // Update local state
      setPhotoOrder(prev => prev.filter(id => id !== photoId));
      setAlbumPhotos(prev => prev.filter(p => p.id !== photoId));
      if (coverPhotoId === photoId) {
        setCoverPhotoId(null);
      }
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Upload photos directly from PC
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const maxSize = 10 * 1024 * 1024; // 10 MB
    
    const validFiles = [];
    const errors = [];
    
    for (const file of Array.from(files)) {
      if (!validTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(heic|heif)$/)) {
        errors.push(`${file.name}: Format non supporté (JPEG, PNG, WEBP, HEIC uniquement)`);
        continue;
      }
      if (file.size > maxSize) {
        errors.push(`${file.name}: Fichier trop volumineux (max 10 Mo)`);
        continue;
      }
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) {
      toast.error("Aucun fichier valide sélectionné");
      if (errors.length > 0) {
        errors.forEach(e => toast.error(e));
      }
      return;
    }
    
    setUploading(true);
    setUploadProgress({ current: 0, total: validFiles.length, errors: [] });
    
    const uploadedPhotoIds = [];
    const uploadErrors = [];
    
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadProgress(prev => ({ ...prev, current: i + 1 }));
      
      try {
        // Upload to server/Cloudinary
        const formData = new FormData();
        formData.append('file', file);
        formData.append('album_id', selectedAlbum.id);
        
        const response = await uploadPhoto(formData);
        if (response.data && response.data.id) {
          uploadedPhotoIds.push(response.data.id);
        }
      } catch (error) {
        const errMsg = error.response?.data?.detail || `Erreur lors de l'upload de ${file.name}`;
        uploadErrors.push(errMsg);
        console.error(`Upload error for ${file.name}:`, error);
      }
    }
    
    setUploading(false);
    
    if (uploadedPhotoIds.length > 0) {
      toast.success(`${uploadedPhotoIds.length} photo(s) importée(s) avec succès`);
      // Update local state
      setPhotoOrder(prev => [...prev, ...uploadedPhotoIds]);
      openAlbum(selectedAlbum);
      loadData();
    }
    
    if (uploadErrors.length > 0) {
      setUploadProgress(prev => ({ ...prev, errors: uploadErrors }));
      toast.error(`${uploadErrors.length} erreur(s) d'import`);
    }
  };

  // Drag and drop reordering
  const handleDragStart = (photoId) => {
    setDraggedPhotoId(photoId);
  };

  const handleDragOver = (e, targetPhotoId) => {
    e.preventDefault();
    if (!draggedPhotoId || draggedPhotoId === targetPhotoId) return;
    
    const newOrder = [...photoOrder];
    const draggedIndex = newOrder.indexOf(draggedPhotoId);
    const targetIndex = newOrder.indexOf(targetPhotoId);
    
    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedPhotoId);
      setPhotoOrder(newOrder);
      setHasUnsavedChanges(true);
    }
  };

  const handleDragEnd = () => {
    setDraggedPhotoId(null);
  };

  // Set cover photo
  const handleSetCover = (photoId) => {
    setCoverPhotoId(photoId);
    // Move to first position
    const newOrder = [photoId, ...photoOrder.filter(id => id !== photoId)];
    setPhotoOrder(newOrder);
    setHasUnsavedChanges(true);
    toast.success("Photo de couverture définie");
  };

  // Save changes (order and cover)
  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await updateAlbum(selectedAlbum.id, {
        cover_photo_id: coverPhotoId,
        photo_order: photoOrder
      });
      setHasUnsavedChanges(false);
      toast.success("Modifications enregistrées");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Handle photo rotation
  const handleRotatePhoto = async (photoId, action) => {
    try {
      await rotatePhoto(photoId, action);
      // Reload album photos to get updated orientation
      if (selectedAlbum) {
        const photosRes = await getAlbumPhotos(selectedAlbum.id);
        setAlbumPhotos(photosRes.data);
      }
      toast.success(action === "reset" ? "Orientation réinitialisée" : `Photo ${action === "left" ? "tournée à gauche" : action === "right" ? "tournée à droite" : action === "flip_horizontal" ? "miroir horizontal" : "miroir vertical"}`);
    } catch (error) {
      toast.error("Erreur lors de la rotation");
    }
  };

  // Sort photos by order, but include all photos in album (even if not in photoOrder)
  const sortedPhotos = (() => {
    if (photoOrder.length > 0) {
      // Get photos in order
      const orderedPhotos = photoOrder.map(id => albumPhotos.find(p => p.id === id)).filter(Boolean);
      // Add any photos not in photoOrder (at the end)
      const orderedIds = new Set(photoOrder);
      const unorderedPhotos = albumPhotos.filter(p => !orderedIds.has(p.id));
      return [...orderedPhotos, ...unorderedPhotos];
    }
    return albumPhotos;
  })();

  // Handle generate article from album
  const handleGenerateArticle = () => {
    if (sortedPhotos.length === 0) {
      toast.error("Ajoutez d'abord des photos à l'album");
      return;
    }
    
    // Navigate to batch generator or AI editor with album context
    navigate("/ai-editor", { 
      state: { 
        photos: sortedPhotos,
        album: selectedAlbum,
        category_id: selectedAlbum.category_id,
        destination_id: selectedAlbum.destination_id,
        venue_name: selectedAlbum.venue_name,
        company: selectedAlbum.company,
        dates: selectedAlbum.dates,
        cover_photo_id: coverPhotoId,
        photo_order: photoOrder
      }
    });
  };

  // Export album photos list to JSON
  const handleExportPhotosList = () => {
    if (sortedPhotos.length === 0) {
      toast.error("Aucune photo à exporter");
      return;
    }

    // Build export data
    const exportData = {
      album_id: selectedAlbum.id,
      album_title: selectedAlbum.name,
      photos_count: sortedPhotos.length,
      photos: sortedPhotos.map((photo, index) => {
        // Extract date and time from created_at or taken_at
        const photoDate = photo.taken_at || photo.created_at;
        let dateStr = "";
        let timeStr = "";
        if (photoDate) {
          try {
            const d = new Date(photoDate);
            dateStr = d.toISOString().split("T")[0]; // YYYY-MM-DD
            timeStr = d.toTimeString().split(" ")[0]; // HH:MM:SS
          } catch (e) {
            // Keep empty if parsing fails
          }
        }

        return {
          order: index + 1,
          photo_id: photo.id,
          url: photo.cloudinary_url || photo.url || null,
          filename: photo.filename || photo.original_filename || null,
          is_cover: photo.id === coverPhotoId,
          date: dateStr,
          time: timeStr
        };
      })
    };

    // Create and download JSON file
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `album-${selectedAlbum.id}-photos.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Export de ${sortedPhotos.length} photos réussi`);
  };

  // Album detail view
  if (selectedAlbum) {
    return (
      <div className="space-y-6" data-testid="album-detail-view">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setSelectedAlbum(null)} className="gap-2">
            <ChevronRight className="h-4 w-4 rotate-180" /> Retour aux albums
          </Button>
          <div className="flex gap-2 flex-wrap">
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="image/jpeg,image/png,image/webp,.heic,.heif"
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files)}
            />
            <Button 
              onClick={() => fileInputRef.current?.click()} 
              className="gap-2"
              disabled={uploading}
              data-testid="upload-photos-btn"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Import {uploadProgress.current}/{uploadProgress.total}</>
              ) : (
                <><Upload className="h-4 w-4" /> Importer des photos</>
              )}
            </Button>
            <Button onClick={openAddPhotos} variant="outline" className="gap-2" data-testid="add-from-library-btn">
              <ImagePlus className="h-4 w-4" /> Depuis la médiathèque
            </Button>
            {hasUnsavedChanges && (
              <Button 
                onClick={handleSaveChanges} 
                className="gap-2 bg-green-600 hover:bg-green-700"
                disabled={saving}
                data-testid="save-changes-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Enregistrer
              </Button>
            )}
            <Button 
              className="gap-2 bg-accent hover:bg-accent/90"
              onClick={handleGenerateArticle}
              disabled={sortedPhotos.length === 0}
              data-testid="generate-article-btn"
            >
              <Sparkles className="h-4 w-4" /> Générer article
            </Button>
            <Button 
              variant="outline"
              className="gap-2"
              onClick={handleExportPhotosList}
              disabled={sortedPhotos.length === 0}
              data-testid="export-photos-btn"
            >
              <Download className="h-4 w-4" /> Exporter la liste des photos
            </Button>
          </div>
        </div>

        {/* Upload errors display */}
        {uploadProgress.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">Erreurs d'import :</h4>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              {uploadProgress.errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
            <Button 
              variant="ghost" 
              size="sm" 
              className="mt-2 text-red-600"
              onClick={() => setUploadProgress(prev => ({ ...prev, errors: [] }))}
            >
              Fermer
            </Button>
          </div>
        )}

        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-start gap-4 mb-6">
            {coverPhotoId && sortedPhotos.find(p => p.id === coverPhotoId) ? (
              <img 
                src={getThumbnailUrl(sortedPhotos.find(p => p.id === coverPhotoId), 150)} 
                alt="Couverture" 
                className="w-24 h-24 object-cover rounded border-2 border-accent"
              />
            ) : selectedAlbum.cover_photo_url ? (
              <img src={selectedAlbum.cover_photo_url} alt="" className="w-24 h-24 object-cover rounded" />
            ) : (
              <div className="w-24 h-24 bg-muted rounded flex items-center justify-center">
                <Image className="h-8 w-8 text-muted-foreground/30" />
              </div>
            )}
            <div className="flex-1">
              <h2 className="font-heading text-2xl font-semibold">{selectedAlbum.name}</h2>
              {selectedAlbum.description && (
                <p className="text-muted-foreground mt-1">{selectedAlbum.description}</p>
              )}
              <div className="flex gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                {selectedAlbum.venue_name && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{selectedAlbum.venue_name}</span>}
                {selectedAlbum.company && <span>{selectedAlbum.company}</span>}
                {selectedAlbum.dates && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{selectedAlbum.dates}</span>}
                {selectedAlbum.destination_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{selectedAlbum.destination_name}</span>}
                <span className="flex items-center gap-1"><Image className="h-3 w-3" />{sortedPhotos.length} photos</span>
              </div>
              {hasUnsavedChanges && (
                <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Modifications non enregistrées
                </p>
              )}
            </div>
          </div>

          {/* Instructions */}
          {sortedPhotos.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-sm text-blue-800">
              <strong>Astuce :</strong> Glissez-déposez les photos pour les réorganiser. Cliquez sur l'étoile pour définir la couverture.
            </div>
          )}

          {/* Photos grid with drag & drop */}
          {sortedPhotos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3" data-testid="photos-grid">
              {sortedPhotos.map((photo, index) => (
                <div 
                  key={photo.id} 
                  className={`relative group aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-move ${
                    draggedPhotoId === photo.id ? 'opacity-50 border-accent' : 
                    coverPhotoId === photo.id ? 'border-accent ring-2 ring-accent/30' : 'border-transparent hover:border-gray-300'
                  }`}
                  draggable
                  onDragStart={() => handleDragStart(photo.id)}
                  onDragOver={(e) => handleDragOver(e, photo.id)}
                  onDragEnd={handleDragEnd}
                  data-testid={`photo-${photo.id}`}
                >
                  <img src={getThumbnailUrl(photo, 200)} alt="" className="w-full h-full object-cover" />
                  
                  {/* Order number */}
                  <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                    {index + 1}
                  </div>
                  
                  {/* Cover indicator */}
                  {coverPhotoId === photo.id && (
                    <div className="absolute top-1 right-8 bg-accent text-white text-xs px-1.5 py-0.5 rounded">
                      Couverture
                    </div>
                  )}
                  
                  {/* Actions on hover */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                    {/* Top row: zoom, cover and delete */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEnlargedPhoto(photo); }}
                        className="p-2 bg-white/90 rounded-full hover:scale-110 transition-transform"
                        title="Agrandir"
                      >
                        <ZoomIn className="h-4 w-4 text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSetCover(photo.id); }}
                        className={`p-2 rounded-full ${coverPhotoId === photo.id ? 'bg-accent' : 'bg-white/90'} hover:scale-110 transition-transform`}
                        title="Définir comme couverture"
                      >
                        <Star className={`h-4 w-4 ${coverPhotoId === photo.id ? 'text-white fill-white' : 'text-amber-500'}`} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemovePhoto(photo.id); }}
                        className="p-2 bg-red-500 rounded-full hover:scale-110 transition-transform"
                        title="Retirer de l'album"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                    {/* Bottom row: rotation controls */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRotatePhoto(photo.id, "left"); }}
                        className="p-1.5 bg-white/90 rounded-full hover:scale-110 transition-transform"
                        title="Tourner à gauche"
                      >
                        <RotateCcw className="h-3 w-3 text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRotatePhoto(photo.id, "right"); }}
                        className="p-1.5 bg-white/90 rounded-full hover:scale-110 transition-transform"
                        title="Tourner à droite"
                      >
                        <RotateCw className="h-3 w-3 text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRotatePhoto(photo.id, "flip_horizontal"); }}
                        className="p-1.5 bg-white/90 rounded-full hover:scale-110 transition-transform"
                        title="Miroir horizontal"
                      >
                        <FlipHorizontal className="h-3 w-3 text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRotatePhoto(photo.id, "reset"); }}
                        className="p-1.5 bg-white/90 rounded-full hover:scale-110 transition-transform"
                        title="Réinitialiser orientation"
                      >
                        <RefreshCw className="h-3 w-3 text-gray-700" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Drag handle indicator */}
                  <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical className="h-4 w-4 text-white drop-shadow" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <Upload className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">
                Aucune photo dans cet album
              </p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => fileInputRef.current?.click()} className="gap-2">
                  <Upload className="h-4 w-4" /> Importer des photos
                </Button>
                <Button onClick={openAddPhotos} variant="outline" className="gap-2">
                  <ImagePlus className="h-4 w-4" /> Depuis la médiathèque
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Add Photos from Library Modal */}
        {showAddPhotos && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-auto">
              <div className="p-4 border-b sticky top-0 bg-white z-10 flex justify-between items-center">
                <h3 className="font-heading text-lg font-semibold">Ajouter depuis la médiathèque</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowAddPhotos(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="p-4">
                <p className="text-sm text-muted-foreground mb-4">
                  {selectedForAdd.length} photo(s) sélectionnée(s) • Cliquez pour sélectionner
                </p>
                {availablePhotos.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 sm:gap-3 max-h-[50vh] overflow-y-auto p-1">
                    {availablePhotos.map(photo => (
                      <button
                        key={photo.id}
                        onClick={() => setSelectedForAdd(prev => 
                          prev.includes(photo.id) ? prev.filter(id => id !== photo.id) : [...prev, photo.id]
                        )}
                        className={`relative aspect-square rounded overflow-hidden border-2 ${
                          selectedForAdd.includes(photo.id) ? "border-accent ring-2 ring-accent/30" : "border-transparent hover:border-gray-300"
                        }`}
                      >
                        <img src={getThumbnailUrl(photo, 150)} alt="" className="w-full h-full object-cover" />
                        {selectedForAdd.includes(photo.id) && (
                          <div className="absolute inset-0 bg-accent/20 flex items-center justify-center">
                            <Check className="h-5 w-5 text-accent" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune photo disponible dans la médiathèque. Importez d'abord des photos.
                  </p>
                )}
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddPhotos(false)}>Annuler</Button>
                <Button onClick={handleAddPhotos} disabled={selectedForAdd.length === 0}>
                  Ajouter {selectedForAdd.length} photo(s)
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Photo Lightbox Modal for Album Detail View */}
        {enlargedPhoto && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setEnlargedPhoto(null)}
            data-testid="photo-lightbox"
          >
            {/* Close button */}
            <button 
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
              onClick={() => setEnlargedPhoto(null)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
            
            {/* Navigation arrows */}
            {sortedPhotos.length > 1 && (
              <>
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIndex = sortedPhotos.findIndex(p => p.id === enlargedPhoto.id);
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : sortedPhotos.length - 1;
                    setEnlargedPhoto(sortedPhotos[prevIndex]);
                  }}
                >
                  <ChevronLeft className="h-8 w-8 text-white" />
                </button>
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIndex = sortedPhotos.findIndex(p => p.id === enlargedPhoto.id);
                    const nextIndex = currentIndex < sortedPhotos.length - 1 ? currentIndex + 1 : 0;
                    setEnlargedPhoto(sortedPhotos[nextIndex]);
                  }}
                >
                  <ChevronRight className="h-8 w-8 text-white" />
                </button>
              </>
            )}
            
            {/* Main image */}
            <div className="max-w-[90vw] max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
              <img 
                src={getFullSizeUrl(enlargedPhoto)}
                alt={enlargedPhoto.filename || "Photo"}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
              {/* Photo info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
                <p className="text-white font-medium">{enlargedPhoto.filename}</p>
                <div className="flex items-center gap-4 text-white/70 text-sm mt-1">
                  <span>Photo {sortedPhotos.findIndex(p => p.id === enlargedPhoto.id) + 1} / {sortedPhotos.length}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Albums list view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-primary">Albums</h2>
          <p className="text-muted-foreground">Organisez vos photos par voyage ou événement</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Nouvel album
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : albums.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {albums.map(album => (
            <div 
              key={album.id} 
              className="bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => openAlbum(album)}
            >
              <div className="aspect-video bg-muted relative">
                {album.cover_photo_url ? (
                  <img src={album.cover_photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="h-12 w-12 text-muted-foreground/30" />
                  </div>
                )}
                <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 rounded text-white text-xs">
                  {album.photo_count} photos
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-medium truncate">{album.name}</h3>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  {album.category_name && <span className="bg-accent/10 text-accent px-2 py-0.5 rounded">{album.category_name}</span>}
                  {album.dates && <span>{album.dates}</span>}
                </div>
              </div>
              <div className="px-3 pb-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="w-full"
                  onClick={(e) => { e.stopPropagation(); handleDeleteAlbum(album.id); }}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Supprimer
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border rounded-lg">
          <Image className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">Aucun album créé</p>
          <Button onClick={() => setShowCreate(true)}>Créer mon premier album</Button>
        </div>
      )}

      {/* Create Album Modal */}
      {showCreate && (
        <CreateAlbumModal
          categories={categories}
          destinations={destinations}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreateAlbum}
        />
      )}
    </div>
  );
}

// Create Album Modal
function CreateAlbumModal({ categories, destinations, onClose, onCreate }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    destination_id: "",
    category_id: "",
    dates: "",
    venue_name: "",
    company: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    onCreate({
      ...form,
      destination_id: form.destination_id || undefined,
      category_id: form.category_id || undefined
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-heading text-lg font-semibold">Nouvel album</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nom de l'album *</label>
            <Input
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              placeholder="Ex: Croisière MSC Meraviglia - Mars 2024"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Textarea
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              placeholder="Courte description de ce voyage..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Catégorie</label>
              <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v})}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Destination</label>
              <Select value={form.destination_id} onValueChange={v => setForm({...form, destination_id: v})}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {destinations.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Dates</label>
              <Input
                value={form.dates}
                onChange={e => setForm({...form, dates: e.target.value})}
                placeholder="Ex: 15-22 mars 2024"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nom du lieu</label>
              <Input
                value={form.venue_name}
                onChange={e => setForm({...form, venue_name: e.target.value})}
                placeholder="Ex: MSC Meraviglia, Hôtel Negresco..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Compagnie (croisière)</label>
            <Input
              value={form.company}
              onChange={e => setForm({...form, company: e.target.value})}
              placeholder="Ex: MSC Croisières"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit">Créer l'album</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Photos Page
function PhotosPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // grid, list
  const [filterSource, setFilterSource] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showBatchImport, setShowBatchImport] = useState(false);
  const [showBatchProcess, setShowBatchProcess] = useState(false);
  const [importUrls, setImportUrls] = useState("");
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [watermarkPending, setWatermarkPending] = useState(null); // null=chargement, N=pending count

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadWatermarkPending(); }, []);

  const loadWatermarkPending = async () => {
    try {
      const { data } = await getWatermarkHistoricPending();
      setWatermarkPending(data.pending ?? 0);
    } catch {
      setWatermarkPending(0);
    }
  };

  const loadData = async () => {
    try {
      const [photosRes, categoriesRes, destinationsRes] = await Promise.all([
        getPhotos(),
        getCategories(),
        getDestinations()
      ]);
      setPhotos(photosRes.data);
      setCategories(categoriesRes.data);
      setDestinations(destinationsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Upload to Cloudinary
  const uploadToCloudinary = async (file) => {
    try {
      const sigRes = await getCloudinarySignature("photos/imports", "image");
      const sig = sigRes.data;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);

      const cloudinaryRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );
      
      const cloudinaryData = await cloudinaryRes.json();
      
      if (cloudinaryData.error) {
        throw new Error(cloudinaryData.error.message);
      }

      // Save to database
      await saveCloudinaryPhoto({
        public_id: cloudinaryData.public_id,
        secure_url: cloudinaryData.secure_url,
        width: cloudinaryData.width,
        height: cloudinaryData.height,
        format: cloudinaryData.format,
        bytes: cloudinaryData.bytes,
        original_filename: file.name,
        folder: sig.folder
      });

      return true;
    } catch (error) {
      console.error("Upload error:", error);
      return false;
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    const totalFiles = Array.from(files).filter(f => f.type.startsWith("image/")).length;

    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      
      // Check Cloudinary status
      const statusRes = await getCloudinaryStatus();
      
      if (statusRes.data.configured) {
        // Upload to Cloudinary
        const success = await uploadToCloudinary(file);
        if (success) successCount++;
      } else {
        // Fallback to local storage
        const formData = new FormData();
        formData.append("file", file);
        try {
          await uploadPhoto(formData);
          successCount++;
        } catch (error) {
          toast.error(`Erreur pour ${file.name}`);
        }
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount}/${totalFiles} photo${successCount > 1 ? 's' : ''} importée${successCount > 1 ? 's' : ''}`);
      loadData();
    }
    setUploading(false);
  };

  const handleBatchImportUrls = async () => {
    if (!importUrls.trim()) return;
    setImporting(true);
    
    const urls = importUrls.split("\n").filter(url => url.trim().length > 0);
    
    try {
      const res = await batchImportPhotos({ 
        source_type: "url_list", 
        urls: urls 
      });
      
      toast.success(`${res.data.imported} photo(s) importée(s)`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} erreur(s)`);
      }
      
      setImportUrls("");
      setShowBatchImport(false);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleSyncCloudinary = async () => {
    setSyncing(true);
    try {
      const res = await syncFromCloudinary("");
      toast.success(res.data.message);
      loadData();
      setShowBatchImport(false);
    } catch (error) {
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await deletePhoto(id);
      toast.success("Photo supprimée");
      setSelectedPhotos(prev => prev.filter(p => p !== id));
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedPhotos.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedPhotos.length} photo(s) ?`)) return;
    
    for (const id of selectedPhotos) {
      try {
        await deletePhoto(id);
      } catch (error) {
        console.error(error);
      }
    }
    
    toast.success(`${selectedPhotos.length} photo(s) supprimée(s)`);
    setSelectedPhotos([]);
    loadData();
  };

  const toggleSelectPhoto = (id) => {
    setSelectedPhotos(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedPhotos(filteredPhotos.map(p => p.id));
  };

  const deselectAll = () => {
    setSelectedPhotos([]);
  };

  // Filter photos
  const filteredPhotos = photos.filter(photo => {
    if (filterSource !== "all" && photo.source !== filterSource) return false;
    if (filterStatus !== "all" && photo.status !== filterStatus) return false;
    return true;
  });

  return (
    <div data-testid="photos-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
          Bibliothèque Média
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (watermarkPending === 0) return;
              if (!window.confirm(
                "Appliquer le watermark ITERCATUS a tous les medias historiques ?\n\n" +
                "- Les medias marques comme partenaires (credit.source=partner) seront ignores.\n" +
                "- L'URL master (original) reste intacte.\n" +
                "- Seule la public_cloudinary_url sera recalculee.\n\n" +
                "Cette operation est idempotente (peut etre rejouee)."
              )) return;
              try {
                const { data } = await runWatermarkHistoricMigration();
                toast.success(
                  `Watermark applique : ${data.updated} mis a jour, ${data.skipped} ignores` +
                  (data.errors?.length ? `, ${data.errors.length} erreurs` : "")
                );
                loadData();
                loadWatermarkPending();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erreur pendant la migration");
              }
            }}
            disabled={watermarkPending === 0}
            title={watermarkPending === 0 ? "Tous les medias sont a jour" : `Appliquer le watermark historique (${watermarkPending ?? "…"})`}
            data-testid="watermark-historic-migration-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {watermarkPending === 0
              ? "Tous les medias sont a jour"
              : `Appliquer le watermark historique${watermarkPending == null ? "" : ` (${watermarkPending})`}`}
          </Button>
          <Button variant="outline" onClick={() => setShowBatchImport(true)}>
            <Cloud className="h-4 w-4 mr-2" />
            Import batch
          </Button>
          {selectedPhotos.length > 0 && (
            <Button className="bg-accent hover:bg-accent/90" onClick={() => setShowBatchProcess(true)}>
              <Sparkles className="h-4 w-4 mr-2" />
              Générer article ({selectedPhotos.length})
            </Button>
          )}
        </div>
      </div>

      {/* Batch Import Modal */}
      {showBatchImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold">Import par lot</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowBatchImport(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Upload zone */}
              <div>
                <h3 className="font-medium mb-3">Upload multiple depuis votre ordinateur</h3>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? "border-accent bg-accent/5" : "border-border"
                  }`}
                  onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { 
                    e.preventDefault(); 
                    setDragActive(false); 
                    handleUpload(Array.from(e.dataTransfer.files)); 
                    setShowBatchImport(false);
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => { 
                      handleUpload(Array.from(e.target.files)); 
                      setShowBatchImport(false);
                    }}
                    className="hidden"
                    id="batch-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="batch-upload" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="h-12 w-12 text-accent mx-auto mb-4 animate-spin" />
                    ) : (
                      <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    )}
                    <p className="text-primary font-medium mb-1">
                      {uploading ? "Upload en cours..." : "Glissez vos photos ici"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ou cliquez pour sélectionner plusieurs fichiers
                    </p>
                  </label>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              {/* Sync from Cloudinary */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <Cloud className="h-5 w-5" />
                  Synchroniser depuis Cloudinary
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  Si vous avez uploadé vos photos directement sur Cloudinary, cliquez ici pour les importer automatiquement.
                </p>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  onClick={handleSyncCloudinary}
                  disabled={syncing}
                >
                  {syncing ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Synchronisation...</>
                  ) : (
                    <><Cloud className="h-4 w-4 mr-2" /> Synchroniser maintenant</>
                  )}
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              {/* URL Import */}
              <div>
                <h3 className="font-medium mb-3">Import depuis URLs</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Collez les URLs directes de vos photos (une par ligne).
                </p>
                <Textarea
                  placeholder="https://exemple.com/photo1.jpg&#10;https://exemple.com/photo2.jpg&#10;..."
                  value={importUrls}
                  onChange={(e) => setImportUrls(e.target.value)}
                  rows={4}
                  className="font-mono text-sm"
                />
                <Button 
                  className="mt-3 w-full" 
                  variant="outline"
                  onClick={handleBatchImportUrls}
                  disabled={importing || !importUrls.trim()}
                >
                  {importing ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Import en cours...</>
                  ) : (
                    <>Importer {importUrls.split("\n").filter(u => u.trim()).length || 0} photo(s)</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Process Modal */}
      {showBatchProcess && (
        <BatchArticleGenerator
          photos={photos.filter(p => selectedPhotos.includes(p.id))}
          categories={categories}
          destinations={destinations}
          onClose={() => setShowBatchProcess(false)}
          onSuccess={() => {
            setShowBatchProcess(false);
            setSelectedPhotos([]);
            toast.success("Brouillon créé !");
          }}
          getPhotoUrl={getThumbnailUrl}
        />
      )}

      {/* Filters and Selection Bar */}
      <div className="bg-white border border-border rounded-lg p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Source:</span>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="manual">Manuel</SelectItem>
                <SelectItem value="mobile">Mobile</SelectItem>
                <SelectItem value="cloudinary">Cloudinary</SelectItem>
                <SelectItem value="batch-import">Import batch</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Statut:</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="uploaded">Uploadée</SelectItem>
                <SelectItem value="imported">Importée</SelectItem>
                <SelectItem value="used">Utilisée</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{filteredPhotos.length} photo(s)</span>
            {selectedPhotos.length > 0 && (
              <>
                <span className="text-accent font-medium">• {selectedPhotos.length} sélectionnée(s)</span>
                <Button variant="ghost" size="sm" onClick={deselectAll}>Désélectionner</Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={handleDeleteSelected}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </>
            )}
            {selectedPhotos.length === 0 && filteredPhotos.length > 0 && (
              <Button variant="ghost" size="sm" onClick={selectAll}>Tout sélectionner</Button>
            )}
          </div>
        </div>
      </div>

      {/* Photos Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array(12).fill(0).map((_, i) => <Skeleton key={i} className="aspect-square" />)}
        </div>
      ) : filteredPhotos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredPhotos.map((photo) => {
            const isSelected = selectedPhotos.includes(photo.id);
            
            return (
              <div 
                key={photo.id} 
                className={`group relative aspect-square overflow-hidden rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected ? "border-accent ring-2 ring-accent/30" : "border-border hover:border-accent/50"
                }`}
                onClick={() => toggleSelectPhoto(photo.id)}
              >
                {getThumbnailUrl(photo, 300) ? (
                  <img src={getThumbnailUrl(photo, 300)} alt={photo.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <Image className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                
                {/* Selection checkbox */}
                <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isSelected ? "bg-accent border-accent" : "bg-white/80 border-border"
                }`}>
                  {isSelected && <Check className="h-4 w-4 text-white" />}
                </div>

                {/* Status badge */}
                <div className={`absolute top-2 right-2 px-2 py-0.5 text-xs rounded ${
                  photo.status === "used" ? "bg-green-500 text-white" : 
                  photo.storage_type === "cloudinary" ? "bg-blue-500 text-white" : 
                  "bg-white/80 text-primary"
                }`}>
                  {photo.status === "used" ? "Utilisée" : photo.storage_type === "cloudinary" ? "Cloud" : "Local"}
                </div>

                {/* Info overlay on hover */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-xs truncate">{photo.filename}</p>
                  <p className="text-white/70 text-xs">
                    {photo.created_at ? format(new Date(photo.created_at), "dd/MM/yyyy", { locale: fr }) : ""}
                  </p>
                </div>

                {/* Delete button */}
                <button
                  className="absolute bottom-2 right-2 w-7 h-7 bg-destructive rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex"
                  onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                >
                  <Trash2 className="h-3 w-3 text-white" />
                </button>

                {/* Edit button (Bloc Media Detail) */}
                <button
                  className="absolute bottom-2 left-2 w-7 h-7 bg-primary rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex"
                  onClick={(e) => { e.stopPropagation(); window.location.href = `/dashboard/media/${photo.id}`; }}
                  data-testid={`btn-edit-media-${photo.id}`}
                  title="Éditer le média (métadonnées, floutage, watermark)"
                >
                  <Pencil className="h-3 w-3 text-white" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-border rounded-lg">
          <Image className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucune photo</h3>
          <p className="text-muted-foreground mb-4">Importez vos photos de voyage pour commencer.</p>
          <Button className="bg-accent hover:bg-accent/90 gap-2" onClick={() => setShowBatchImport(true)}>
            <Cloud className="h-4 w-4" />
            Import batch
          </Button>
        </div>
      )}
    </div>
  );
}


// Article Revision Modal - Add photos to existing article
function ArticleRevisionModal({ article, onClose, onSuccess }) {
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState(article.photos || []);
  const [photosToRemove, setPhotosToRemove] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revising, setRevising] = useState(false);
  const [revisionMode, setRevisionMode] = useState("correct"); // correct, append, replace
  const [corrections, setCorrections] = useState("");
  const [showPhotoLibrary, setShowPhotoLibrary] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const fileInputRef = useRef(null);
  const [editorialHints, setEditorialHints] = useState({
    highlights: "",
    lowlights: "",
    must_mention: ""
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPhotos(); }, []);

  const loadPhotos = async () => {
    try {
      const res = await getPhotos();
      // Filter out photos already in the article
      const existingPhotoIds = article.photos?.map(p => p.id) || [];
      setPhotos(res.data.filter(p => !existingPhotoIds.includes(p.id)));
    } catch (error) {
      console.error("Error loading photos:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle direct upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhotos(true);
    const uploadedIds = [];
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await uploadPhoto(formData);
        if (res.data?.id) {
          uploadedIds.push(res.data.id);
        }
      }
      
      // Add uploaded photos to selection and reload library
      if (uploadedIds.length > 0) {
        setSelectedPhotos(prev => [...prev, ...uploadedIds]);
        await loadPhotos();
        toast.success(`${uploadedIds.length} photo(s) uploadée(s) et ajoutée(s)`);
        
        // Switch to "append" mode if not already
        if (revisionMode !== "append") {
          setRevisionMode("append");
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload des photos");
    } finally {
      setUploadingPhotos(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const togglePhoto = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]
    );
  };

  const toggleRemoveExisting = (photoId) => {
    setPhotosToRemove(prev =>
      prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]
    );
  };

  const handleRevise = async () => {
    // Validate based on mode
    if (revisionMode === "append" && selectedPhotos.length === 0) {
      toast.error("Sélectionnez au moins une nouvelle photo pour enrichir l'article");
      return;
    }
    
    if (revisionMode === "correct" && !corrections.trim()) {
      toast.error("Veuillez indiquer les corrections à apporter");
      return;
    }

    setRevising(true);
    try {
      await reviseArticle({
        article_id: article.id,
        new_photo_ids: selectedPhotos.length > 0 ? selectedPhotos : [],
        remove_photo_ids: photosToRemove.length > 0 ? photosToRemove : [],
        revision_mode: revisionMode,
        corrections: corrections || undefined,
        highlights: editorialHints.highlights || undefined,
        lowlights: editorialHints.lowlights || undefined,
        must_mention: editorialHints.must_mention || undefined
      });
      toast.success("Article révisé avec succès !");
      onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur lors de la révision";
      toast.error(detail);
    } finally {
      setRevising(false);
    }
  };

  const canSubmit = () => {
    if (revisionMode === "correct") return corrections.trim().length > 0;
    if (revisionMode === "append") return selectedPhotos.length > 0;
    return true; // replace mode always ok
  };

  const selectedNewPhotos = photos.filter(p => selectedPhotos.includes(p.id));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-auto shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                Réviser l'article
              </h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-lg truncate">
                {article.title}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Revision mode */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">1</span>
              Type de révision
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                revisionMode === "correct" ? "border-blue-500 bg-blue-50 shadow-md" : "border-gray-200 hover:border-blue-300"
              }`}>
                <input type="radio" name="revision_mode" value="correct" checked={revisionMode === "correct"} onChange={() => setRevisionMode("correct")} className="sr-only" />
                <div className="flex items-center gap-2 mb-2">
                  <Edit3 className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-blue-700">Corriger</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Modifier le texte existant sans refaire l'article
                </span>
              </label>
              
              <label className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                revisionMode === "append" ? "border-green-500 bg-green-50 shadow-md" : "border-gray-200 hover:border-green-300"
              }`}>
                <input type="radio" name="revision_mode" value="append" checked={revisionMode === "append"} onChange={() => setRevisionMode("append")} className="sr-only" />
                <div className="flex items-center gap-2 mb-2">
                  <ImagePlus className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-700">Enrichir</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Ajouter de nouvelles photos et du contenu
                </span>
              </label>
              
              <label className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                revisionMode === "replace" ? "border-orange-500 bg-orange-50 shadow-md" : "border-gray-200 hover:border-orange-300"
              }`}>
                <input type="radio" name="revision_mode" value="replace" checked={revisionMode === "replace"} onChange={() => setRevisionMode("replace")} className="sr-only" />
                <div className="flex items-center gap-2 mb-2">
                  <RotateCcw className="h-5 w-5 text-orange-600" />
                  <span className="font-semibold text-orange-700">Réécrire</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Regénérer entièrement l'article
                </span>
              </label>
            </div>
          </div>

          {/* Current photos - Larger thumbnails */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-bold">2</span>
              Photos actuelles de l'article
              <span className="text-sm text-muted-foreground ml-1">({existingPhotos.length - photosToRemove.length} photo{existingPhotos.length - photosToRemove.length > 1 ? 's' : ''})</span>
            </h3>
            
            {existingPhotos.length > 0 ? (
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {existingPhotos.map(photo => {
                  const isMarkedForRemoval = photosToRemove.includes(photo.id);
                  return (
                    <div 
                      key={photo.id}
                      className={`relative group aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        isMarkedForRemoval ? "border-red-400 opacity-50" : "border-green-400"
                      }`}
                    >
                      <img 
                        src={getThumbnailUrl(photo, 200)}
                        alt={photo.filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <button
                        type="button"
                        onClick={() => toggleRemoveExisting(photo.id)}
                        className={`absolute top-1 right-1 p-1.5 rounded-full transition-all ${
                          isMarkedForRemoval 
                            ? "bg-red-500 text-white" 
                            : "bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100 hover:bg-red-100"
                        }`}
                        title={isMarkedForRemoval ? "Annuler la suppression" : "Retirer de l'article"}
                      >
                        {isMarkedForRemoval ? <RotateCcw className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      </button>
                      {isMarkedForRemoval && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">À retirer</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground bg-gray-50 p-4 rounded-lg">Aucune photo dans cet article</p>
            )}
          </div>

          {/* Add new photos section */}
          <div className={`border-2 border-dashed rounded-xl p-4 transition-colors ${
            revisionMode === "append" ? "border-green-300 bg-green-50/50" : "border-gray-200"
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold">3</span>
                Ajouter de nouvelles photos
                {selectedPhotos.length > 0 && (
                  <span className="text-sm bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    +{selectedPhotos.length} sélectionnée{selectedPhotos.length > 1 ? 's' : ''}
                  </span>
                )}
                {revisionMode === "append" && <span className="text-red-500 ml-1">*</span>}
              </h3>
              <div className="flex gap-2">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                />
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhotos}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  {uploadingPhotos ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Upload...</>
                  ) : (
                    <><Upload className="h-4 w-4" /> Uploader des photos</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPhotoLibrary(!showPhotoLibrary)}
                  className="gap-2"
                >
                  <ImagePlus className="h-4 w-4" />
                  {showPhotoLibrary ? "Masquer" : "Bibliothèque"}
                </Button>
              </div>
            </div>

            {/* Selected new photos preview */}
            {selectedNewPhotos.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">Photos à ajouter :</p>
                <div className="flex flex-wrap gap-2">
                  {selectedNewPhotos.map(photo => (
                    <div key={photo.id} className="relative group">
                      <img 
                        src={getThumbnailUrl(photo, 100)}
                        alt=""
                        className="h-16 w-16 object-cover rounded-lg border-2 border-purple-400"
                      />
                      <button
                        type="button"
                        onClick={() => togglePhoto(photo.id)}
                        className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo library */}
            {showPhotoLibrary && (
              <div className="border rounded-lg bg-white">
                {loading ? (
                  <div className="p-8 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : photos.length > 0 ? (
                  <div className="grid grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-2 p-3 max-h-64 overflow-y-auto">
                    {photos.map((photo) => (
                      <button
                        key={photo.id}
                        type="button"
                        onClick={() => togglePhoto(photo.id)}
                        className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                          selectedPhotos.includes(photo.id) 
                            ? "border-purple-500 ring-2 ring-purple-300 scale-95" 
                            : "border-transparent hover:border-purple-300"
                        }`}
                      >
                        <img src={getThumbnailUrl(photo, 120)} alt="" className="w-full h-full object-cover" />
                        {selectedPhotos.includes(photo.id) && (
                          <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                            <Check className="h-6 w-6 text-white drop-shadow-lg" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-6 text-center">
                    Toutes les photos disponibles sont déjà dans l'article.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Instructions field */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">4</span>
              {revisionMode === "correct" ? "Corrections à apporter" : revisionMode === "append" ? "Instructions d'enrichissement" : "Instructions de réécriture"}
              {revisionMode === "correct" && <span className="text-red-500 ml-1">*</span>}
            </h3>
            <Textarea
              value={corrections}
              onChange={e => setCorrections(e.target.value)}
              placeholder={
                revisionMode === "correct" 
                  ? "Ex: Corriger le nom du navire (MSC Meraviglia et non Meravilia), reformuler l'introduction..."
                  : revisionMode === "append"
                  ? "Ex: Les nouvelles photos montrent la piscine rénovée. Ajouter une section sur les activités nautiques..."
                  : "Ex: Mettre plus l'accent sur les activités, ton plus décontracté, ajouter des conseils pratiques..."
              }
              rows={4}
              className="w-full"
            />
          </div>

          {/* Advanced options */}
          <details className="border rounded-lg">
            <summary className="font-medium cursor-pointer p-4 hover:bg-gray-50 flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              Options avancées
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 pt-0 border-t">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Points forts à mettre en avant</label>
                <Input
                  value={editorialHints.highlights}
                  onChange={e => setEditorialHints(prev => ({...prev, highlights: e.target.value}))}
                  placeholder="Ex: Nouvelle piscine, restaurant rénové..."
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Éléments à mentionner</label>
                <Input
                  value={editorialHints.must_mention}
                  onChange={e => setEditorialHints(prev => ({...prev, must_mention: e.target.value}))}
                  placeholder="Ex: Prix mis à jour, nouvelle activité..."
                />
              </div>
            </div>
          </details>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {photosToRemove.length > 0 && (
                <span className="text-red-600">{photosToRemove.length} photo(s) à retirer</span>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                onClick={handleRevise}
                disabled={revising || !canSubmit()}
              >
                {revising ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Révision en cours...</>
                ) : (
                  <><RefreshCw className="h-4 w-4" /> Réviser l'article</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Comments Page
function CommentsPage() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadComments(); }, []);

  const loadComments = async () => {
    try {
      const res = await getPendingComments();
      setComments(res.data);
    } catch (error) {
      console.error("Error loading comments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (id, status) => {
    try {
      await moderateComment(id, status);
      toast.success(status === "approved" ? "Commentaire approuvé" : "Commentaire rejeté");
      loadComments();
    } catch (error) {
      toast.error("Erreur lors de la modération");
    }
  };

  return (
    <div data-testid="comments-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-6">Commentaires en attente</h1>

      {loading ? (
        <div className="space-y-4">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="bg-white border border-border rounded-lg p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="font-medium text-primary">{comment.author_name}</p>
                  <p className="text-sm text-muted-foreground">{comment.author_email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleModerate(comment.id, "approved")} className="text-green-600 border-green-600">
                    <Check className="h-4 w-4 mr-1" />Approuver
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleModerate(comment.id, "rejected")} className="text-destructive border-destructive">
                    <X className="h-4 w-4 mr-1" />Rejeter
                  </Button>
                </div>
              </div>
              <p className="text-muted-foreground">{comment.content}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-border rounded-lg">
          <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucun commentaire</h3>
          <p className="text-muted-foreground">Tous les commentaires ont été modérés.</p>
        </div>
      )}
    </div>
  );
}

// Members Page
function MembersPage() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadMemberships(); }, []);

  const loadMemberships = async () => {
    try {
      const res = await getPendingMemberships();
      setMemberships(res.data);
    } catch (error) {
      console.error("Error loading memberships:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (id) => {
    try {
      await validateMembership(id);
      toast.success("Adhésion validée");
      loadMemberships();
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Rejeter cette demande d'adhésion ?")) return;
    try {
      await rejectMembership(id);
      toast.success("Adhésion refusée");
      loadMemberships();
    } catch (error) {
      toast.error("Erreur lors du refus");
    }
  };

  return (
    <div data-testid="members-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-6">Demandes d'adhésion</h1>

      {loading ? (
        <div className="space-y-4">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : memberships.length > 0 ? (
        <div className="space-y-4">
          {memberships.map((membership) => (
            <div key={membership.id} className="bg-white border border-border rounded-lg p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-heading text-lg font-semibold text-primary">{membership.user_name}</p>
                  <p className="text-sm text-muted-foreground">{membership.user_email}</p>
                  {membership.city && (
                    <p className="text-sm text-muted-foreground mt-1">
                      <MapPin className="h-3 w-3 inline mr-1" />
                      {membership.city}, {membership.country}
                    </p>
                  )}
                  {membership.motivation && (
                    <p className="text-sm text-muted-foreground mt-2 italic">"{membership.motivation}"</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => handleValidate(membership.id)} className="bg-green-600 hover:bg-green-700">
                    <Check className="h-4 w-4 mr-1" />Valider
                  </Button>
                  <Button variant="outline" onClick={() => handleReject(membership.id)} className="text-destructive border-destructive">
                    <X className="h-4 w-4 mr-1" />Refuser
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-border rounded-lg" data-testid="members-empty-state">
          <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucune demande d'adhésion</h3>
          <p className="text-muted-foreground">Aucune demande en attente pour le moment.</p>
        </div>
      )}
    </div>
  );
}
// Product Review AI Generator
function ProductReviewGenerator({ photos, categories, onClose, onSuccess }) {
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [orderedPhotos, setOrderedPhotos] = useState([]);
  const [coverPhotoId, setCoverPhotoId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiModels, setAIModels] = useState(null);
  const [form, setForm] = useState({
    category: "electronics",
    brand: "",
    model_name: "",
    highlights: "",
    lowlights: "",
    usage_context: "",
    price: "",
    purchase_link: "",
    editorial_angle: "",
    ai_model: "gpt-4o"
  });

  useEffect(() => {
    loadAIModels();
  }, []);

  const loadAIModels = async () => {
    try {
      const res = await getAIModels();
      setAIModels(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const togglePhoto = (photo) => {
    if (selectedPhotos.find(p => p.id === photo.id)) {
      setSelectedPhotos(prev => prev.filter(p => p.id !== photo.id));
      setOrderedPhotos(prev => prev.filter(p => p.id !== photo.id));
      if (coverPhotoId === photo.id) {
        setCoverPhotoId(orderedPhotos[0]?.id || null);
      }
    } else {
      setSelectedPhotos(prev => [...prev, photo]);
      setOrderedPhotos(prev => [...prev, photo]);
      if (!coverPhotoId) {
        setCoverPhotoId(photo.id);
      }
    }
  };

  const setCover = (photoId) => {
    setCoverPhotoId(photoId);
    const photo = orderedPhotos.find(p => p.id === photoId);
    if (photo) {
      setOrderedPhotos([photo, ...orderedPhotos.filter(p => p.id !== photoId)]);
    }
  };

  const handleGenerate = async () => {
    if (selectedPhotos.length === 0) {
      toast.error("Sélectionnez au moins une photo");
      return;
    }
    if (!form.category) {
      toast.error("Sélectionnez une catégorie");
      return;
    }

    setGenerating(true);
    try {
      await generateProductReview({
        photo_ids: orderedPhotos.map(p => p.id),
        photo_order: orderedPhotos.map(p => p.id),
        cover_photo_id: coverPhotoId,
        category: form.category,
        brand: form.brand || undefined,
        model_name: form.model_name || undefined,
        highlights: form.highlights || undefined,
        lowlights: form.lowlights || undefined,
        usage_context: form.usage_context || undefined,
        price: form.price || undefined,
        purchase_link: form.purchase_link || undefined,
        editorial_angle: form.editorial_angle || undefined,
        ai_model: form.ai_model
      });
      toast.success("Test produit généré avec succès !");
      onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur lors de la génération";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const categoryOptions = [
    { value: "electronics", label: "Électronique & Gadgets" },
    { value: "luggage", label: "Bagages & Valises" },
    { value: "accessories", label: "Accessoires Voyage" },
    { value: "photo", label: "Photo & Vidéo" },
    { value: "comfort", label: "Confort & Bien-être" },
    { value: "security", label: "Sécurité" },
    { value: "clothing", label: "Vêtements Voyage" },
    { value: "apps", label: "Apps & Services" }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Générer un Test Produit avec IA
              </h2>
              <p className="text-sm text-muted-foreground">
                {selectedPhotos.length} photo(s) sélectionnée(s)
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Photo selection */}
          <div>
            <h3 className="font-medium mb-3">1. Sélectionnez les photos du produit</h3>
            <div className="grid grid-cols-6 md:grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border rounded bg-gray-50">
              {photos.filter(p => p.status !== 'used').map(photo => (
                <button
                  key={photo.id}
                  onClick={() => togglePhoto(photo)}
                  className={`relative aspect-square rounded overflow-hidden border-2 transition-all ${
                    selectedPhotos.find(p => p.id === photo.id) 
                      ? "border-purple-500 ring-2 ring-purple-300" 
                      : "border-transparent hover:border-purple-300"
                  }`}
                >
                  <img src={getThumbnailUrl(photo, 150)} alt="" className="w-full h-full object-cover" />
                  {selectedPhotos.find(p => p.id === photo.id) && (
                    <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Photo order */}
          {selectedPhotos.length > 0 && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h3 className="font-medium text-purple-900 mb-2">Ordre des photos (première = couverture)</h3>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {orderedPhotos.map((photo, idx) => (
                  <div key={photo.id} className={`relative shrink-0 ${photo.id === coverPhotoId ? "ring-2 ring-yellow-400" : ""}`}>
                    <img src={getThumbnailUrl(photo, 64)} alt="" className="h-16 w-16 object-cover rounded" />
                    {photo.id === coverPhotoId && (
                      <div className="absolute top-0 left-0 bg-yellow-400 text-xs px-1 rounded-br font-bold">COUV</div>
                    )}
                    <div className="absolute bottom-0 left-0 bg-black/70 text-white text-xs px-1 rounded-tr">{idx + 1}</div>
                    {photo.id !== coverPhotoId && (
                      <button 
                        onClick={() => setCover(photo.id)}
                        className="absolute top-0 right-0 p-0.5 bg-yellow-400 rounded-bl opacity-0 hover:opacity-100 transition-opacity"
                      >
                        <Star className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Catégorie *</label>
              <Select value={form.category} onValueChange={v => setForm(prev => ({...prev, category: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categoryOptions.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Modèle IA</label>
              <Select value={form.ai_model} onValueChange={v => setForm(prev => ({...prev, ai_model: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {aiModels?.available_models?.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Marque</label>
              <Input 
                value={form.brand} 
                onChange={e => setForm(prev => ({...prev, brand: e.target.value}))}
                placeholder="Ex: Apple, Samsonite, GoPro..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Modèle / Nom du produit</label>
              <Input 
                value={form.model_name} 
                onChange={e => setForm(prev => ({...prev, model_name: e.target.value}))}
                placeholder="Ex: iPhone 15 Pro, Cosmolite..."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Prix</label>
              <Input 
                value={form.price} 
                onChange={e => setForm(prev => ({...prev, price: e.target.value}))}
                placeholder="Ex: 199€"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contexte d'utilisation</label>
              <Input 
                value={form.usage_context} 
                onChange={e => setForm(prev => ({...prev, usage_context: e.target.value}))}
                placeholder="Ex: Testé pendant notre croisière MSC..."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Ce qu'on a aimé</label>
              <Textarea 
                value={form.highlights} 
                onChange={e => setForm(prev => ({...prev, highlights: e.target.value}))}
                rows={2}
                placeholder="Qualité, autonomie, praticité..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ce qu'on a moins aimé</label>
              <Textarea 
                value={form.lowlights} 
                onChange={e => setForm(prev => ({...prev, lowlights: e.target.value}))}
                rows={2}
                placeholder="Prix élevé, poids..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Instructions supplémentaires</label>
            <Textarea 
              value={form.editorial_angle} 
              onChange={e => setForm(prev => ({...prev, editorial_angle: e.target.value}))}
              rows={2}
              placeholder="Mettre l'accent sur..., Comparer avec..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleGenerate}
              disabled={generating || selectedPhotos.length === 0}
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Génération...</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Générer le test</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Users Management Page (Admin Only)
function UsersManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [membershipsByUser, setMembershipsByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    password: "",
    role: "member",
    is_active: true,
    permissions: {}
  });

  // Debounce du champ de recherche pour eviter les requetes a chaque frappe
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Charger roles + memberships UNE SEULE FOIS (elements stables sur la duree de la page)
  useEffect(() => {
    (async () => {
      try {
        const rolesRes = await getAdminRoles();
        setRoles(rolesRes.data.roles);
        setDefaultPermissions(rolesRes.data.permissions);
      } catch { /* silencieux */ }
      try {
        const { data: memberships } = await getMemberships({ limit: 500 });
        const map = {};
        (memberships || []).forEach((m) => { if (m.user_id) map[m.user_id] = m; });
        setMembershipsByUser(map);
      } catch { setMembershipsByUser({}); }
    })();
  }, []);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, roleFilter, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersRes = await getAdminUsers({
        search: debouncedSearch || undefined,
        role: roleFilter !== "all" ? roleFilter : undefined,
        status: statusFilter
      });
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  // Conserve pour les callbacks existants qui appelaient loadData() en post-action
  const loadData = loadUsers;

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      email: "",
      name: "",
      phone: "",
      password: "",
      role: "member",
      is_active: true,
      permissions: defaultPermissions["member"] || {}
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      phone: user.phone || "",
      password: "",
      role: user.role,
      is_active: user.is_active !== false,
      permissions: user.permissions || defaultPermissions[user.role] || {}
    });
    setShowModal(true);
  };

  const handleRoleChange = (newRole) => {
    setFormData(prev => ({
      ...prev,
      role: newRole,
      permissions: defaultPermissions[newRole] || {}
    }));
  };

  const handlePermissionChange = (permKey, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permKey]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          email: formData.email,
          name: formData.name,
          phone: formData.phone || null,
          role: formData.role,
          is_active: formData.is_active,
          permissions: formData.permissions
        };
        await updateAdminUser(editingUser.id, updateData);
        toast.success("Utilisateur mis à jour");
      } else {
        // Create new user
        if (!formData.password || formData.password.length < 6) {
          toast.error("Le mot de passe doit contenir au moins 6 caractères");
          setSaving(false);
          return;
        }
        await createAdminUser({
          email: formData.email,
          name: formData.name,
          phone: formData.phone || null,
          password: formData.password,
          role: formData.role,
          is_active: formData.is_active,
          permissions: formData.permissions
        });
        toast.success("Utilisateur créé");
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm("Suspendre cet utilisateur ?")) return;
    try {
      await suspendUser(userId);
      toast.success("Utilisateur suspendu");
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la suspension";
      toast.error(message);
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await reactivateUser(userId);
      toast.success("Utilisateur réactivé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la réactivation");
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer définitivement ${user.name} (${user.email}) ?`)) return;
    try {
      await deleteAdminUser(user.id);
      toast.success("Utilisateur supprimé");
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la suppression";
      toast.error(message);
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = window.prompt("Nouveau mot de passe (min. 6 caractères):");
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    try {
      await resetUserPassword(userId, newPassword);
      toast.success("Mot de passe réinitialisé");
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation du mot de passe");
    }
  };

  // Check if user can be modified (prevent actions on last admin)
  const canModifyUser = (targetUser) => {
    // Cannot modify own account for delete/suspend
    if (targetUser.id === currentUser?.id) return false;
    return true;
  };

  const canDemoteAdmin = (targetUser) => {
    // Count active admins (admin AND super_admin count as "administrative presence")
    const activeAdmins = users.filter(u => (u.role === "admin" || u.role === "super_admin") && u.is_active !== false);
    // If this is the last admin/super_admin, cannot demote
    if ((targetUser.role === "admin" || targetUser.role === "super_admin") && activeAdmins.length <= 1) return false;
    return true;
  };

  const roleLabels = {
    visitor: "Visiteur",
    member: "Adhérent",
    editor: "Rédacteur",
    moderator: "Modérateur",
    admin: "Administrateur"
  };

  const roleColors = {
    visitor: "bg-gray-100 text-gray-700",
    member: "bg-blue-100 text-blue-700",
    editor: "bg-purple-100 text-purple-700",
    moderator: "bg-orange-100 text-orange-700",
    admin: "bg-red-100 text-red-700"
  };

  const permissionLabels = {
    can_create_article: "Créer des articles",
    can_edit_own_articles: "Modifier ses articles",
    can_edit_all_articles: "Modifier tous les articles",
    can_submit_article: "Soumettre à validation",
    can_validate_article: "Valider les articles",
    can_reject_article: "Refuser les articles",
    can_publish_article: "Publier les articles",
    can_manage_albums: "Gérer les albums",
    can_manage_photos: "Gérer les photos",
    can_view_members: "Voir les membres",
    can_create_member: "Créer des membres",
    can_edit_member: "Modifier les membres",
    can_suspend_member: "Suspendre les membres",
    can_delete_member: "Supprimer les membres",
    can_assign_roles: "Attribuer des rôles",
    can_edit_permissions: "Modifier les permissions"
  };

  // Check admin-only access
  if (currentUser?.role !== "admin" && currentUser?.role !== "super_admin") {
    return (
      <div className="text-center py-12" data-testid="users-page-denied">
        <Shield className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-semibold text-primary">Accès réservé</h2>
        <p className="text-muted-foreground">Cette section est réservée aux administrateurs.</p>
      </div>
    );
  }

  return (
    <div data-testid="users-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">Gestion des utilisateurs</h1>
          <p className="text-muted-foreground">{users.length} utilisateur(s)</p>
        </div>
        <Button onClick={openCreateModal} className="gap-2" data-testid="create-user-btn">
          <UserPlus className="h-4 w-4" /> Nouvel utilisateur
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-border rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-users-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[150px]" data-testid="role-filter">
                <SelectValue placeholder="Rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                {roles.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="suspended">Suspendus</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Utilisateur</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden md:table-cell">Cree le</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">Derniere activite</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">Derniere authentification</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Statut</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((user) => (
                  <tr key={user.id} className={`hover:bg-muted/20 ${user.is_active === false ? 'opacity-60' : ''}`} data-testid={`user-row-${user.id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-primary flex items-center gap-2">
                          {user.name}
                          {membershipsByUser[user.id] ? (
                            <span
                              className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800"
                              title={`Adherent ${membershipsByUser[user.id].member_number} - ${membershipsByUser[user.id].status} - type: ${membershipsByUser[user.id].membership_type || 'standard'}`}
                            >
                              Adherent {membershipsByUser[user.id].member_number}
                              {(() => {
                                const t = membershipsByUser[user.id].membership_type;
                                const labels = { founder: "Fondateur", partner: "Partenaire", donor_partner: "Partenaire donateur" };
                                return t && labels[t] ? ` (${labels[t]})` : "";
                              })()}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${roleColors[user.role] || 'bg-gray-100'}`}>
                        {roleLabels[user.role] || user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
                      {user.created_at ? format(new Date(user.created_at), "dd MMM yyyy", { locale: fr }) : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell" data-testid={`last-seen-${user.id}`}>
                      {(() => {
                        if (!user.last_seen_at) return "Jamais";
                        const diff = (Date.now() - new Date(user.last_seen_at).getTime()) / 1000;
                        if (diff < 300) return <span className="text-green-700 font-medium">En ligne</span>;
                        if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
                        if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
                        return format(new Date(user.last_seen_at), "dd MMM yyyy HH:mm", { locale: fr });
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
                      {user.last_login ? format(new Date(user.last_login), "dd MMM yyyy HH:mm", { locale: fr }) : "Jamais"}
                    </td>
                    <td className="px-4 py-3">
                      {user.is_active === false ? (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1 w-fit">
                          <Ban className="h-3 w-3" /> Suspendu
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">Actif</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(user)}
                          title="Modifier"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleResetPassword(user.id)}
                          title="Réinitialiser le mot de passe"
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {user.is_active !== false ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSuspend(user.id)}
                            disabled={!canModifyUser(user)}
                            title={!canModifyUser(user) ? "Impossible de suspendre votre propre compte" : "Suspendre"}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleReactivate(user.id)}
                            title="Réactiver"
                            className="text-green-600 hover:text-green-700"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(user)}
                          disabled={!canModifyUser(user) || ((user.role === "admin" || user.role === "super_admin") && !canDemoteAdmin(user))}
                          title={
                            !canModifyUser(user) 
                              ? "Impossible de supprimer votre propre compte" 
                              : (user.role === "admin" || user.role === "super_admin") && !canDemoteAdmin(user)
                                ? "Impossible de supprimer le dernier administrateur"
                                : "Supprimer"
                          }
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucun utilisateur trouvé</h3>
            <p className="text-muted-foreground">Modifiez vos critères de recherche</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="user-modal">
            <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-heading text-xl font-semibold">
                {editingUser ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Nom *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Téléphone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    data-testid="user-phone-input"
                  />
                </div>
                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Mot de passe *</label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Min. 6 caractères"
                      required={!editingUser}
                      data-testid="user-password-input"
                    />
                  </div>
                )}
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Rôle *</label>
                <Select 
                  value={formData.role} 
                  onValueChange={handleRoleChange}
                  disabled={(editingUser?.role === "admin" || editingUser?.role === "super_admin") && !canDemoteAdmin(editingUser)}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        <div>
                          <span className="font-medium">{r.label}</span>
                          <span className="text-muted-foreground ml-2 text-xs">- {r.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(editingUser?.role === "admin" || editingUser?.role === "super_admin") && !canDemoteAdmin(editingUser) && (
                  <p className="text-amber-600 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Impossible de rétrograder le dernier administrateur
                  </p>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                  data-testid="user-active-switch"
                />
                <label className="text-sm font-medium">Compte actif</label>
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-primary mb-3">Permissions granulaires</label>
                <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                  {/* Articles */}
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-2">Articles</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {["can_create_article", "can_edit_own_articles", "can_edit_all_articles", "can_submit_article", "can_validate_article", "can_reject_article", "can_publish_article"].map(perm => (
                        <label key={perm} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions[perm] || false}
                            onChange={(e) => handlePermissionChange(perm, e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          {permissionLabels[perm]}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Media */}
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-2">Médias</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {["can_manage_albums", "can_manage_photos"].map(perm => (
                        <label key={perm} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions[perm] || false}
                            onChange={(e) => handlePermissionChange(perm, e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          {permissionLabels[perm]}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Members Management */}
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-2">Gestion des membres</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {["can_view_members", "can_create_member", "can_edit_member", "can_suspend_member", "can_delete_member"].map(perm => (
                        <label key={perm} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions[perm] || false}
                            onChange={(e) => handlePermissionChange(perm, e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          {permissionLabels[perm]}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Administration */}
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-2">Administration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {["can_assign_roles", "can_edit_permissions"].map(perm => (
                        <label key={perm} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions[perm] || false}
                            onChange={(e) => handlePermissionChange(perm, e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          {permissionLabels[perm]}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-user-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingUser ? "Enregistrer" : "Créer"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== Cloudinary Usage Section (Bloc P2) =====================
function CloudinaryUsageSection() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [u, a] = await Promise.all([
        getCloudinaryUsage(),
        getAdminAlerts(false),
      ]);
      setData(u.data);
      setAlerts((a.data || []).filter(al => al.type === "cloudinary_usage"));
    } catch (e) {
      // Ne pas afficher de toast si Cloudinary est simplement non configure
      // (l'endpoint renvoie 200 avec configured=false dans ce cas ; les 502 restent affiches)
      const status = e?.response?.status;
      if (status && status !== 404) {
        toast.error(e?.response?.data?.detail || "Erreur chargement Cloudinary usage");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshCloudinaryUsage();
      toast.success("Usage Cloudinary rafraichi");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Erreur inconnue";
      toast.error(`Erreur refresh Cloudinary : ${detail}`);
      setRefreshing(false);
      return;
    }
    try {
      await load();
    } catch {
      // load a deja son propre toast dans le catch interne
    }
    setRefreshing(false);
  };

  const dismissAlert = async (alertId) => {
    try {
      await dismissAdminAlert(alertId);
      setAlerts(prev => prev.filter(al => al.id !== alertId));
    } catch {
      toast.error("Erreur fermeture alerte");
    }
  };

  const barColor = (pct) => {
    if (pct == null) return "bg-gray-200";
    if (pct >= 90) return "bg-red-500";
    if (pct >= 80) return "bg-orange-500";
    if (pct >= 70) return "bg-amber-400";
    return "bg-emerald-500";
  };

  const timeAgo = (iso) => {
    if (!iso) return "-";
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return "il y a moins d'1h";
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    return `il y a ${d} j`;
  };

  const fmtBytes = (n) => {
    if (n == null) return "-";
    const gb = n / (1024 ** 3);
    if (gb >= 1) return `${gb.toFixed(2)} Go`;
    const mb = n / (1024 ** 2);
    return `${mb.toFixed(1)} Mo`;
  };

  const raw = data?.raw_usage || {};
  const computed = data?.computed || {};
  const rows = [
    {
      key: "storage", label: "Stockage",
      pct: computed.storage_pct,
      usedRaw: (raw.storage || {}).usage,
      limitRaw: (raw.storage || {}).limit,
      formatter: fmtBytes,
    },
    {
      key: "bandwidth", label: "Bande passante (mois)",
      pct: computed.bandwidth_pct,
      usedRaw: (raw.bandwidth || {}).usage,
      limitRaw: (raw.bandwidth || {}).limit,
      formatter: fmtBytes,
    },
    {
      key: "credits", label: "Credits",
      pct: computed.credits_pct,
      usedRaw: (raw.credits || {}).usage,
      limitRaw: (raw.credits || {}).limit,
      formatter: (n) => (n == null ? "-" : Number(n).toFixed(2)),
    },
  ];

  return (
    <div className="bg-white border border-border rounded-lg p-6" data-testid="cloudinary-usage-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold text-primary flex items-center gap-2">
            <Cloud className="h-5 w-5 text-accent" /> Stockage photos (Cloudinary)
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Derniere verification : {timeAgo(data?.last_checked_at)}
            {raw.plan && <> · Plan : <strong>{raw.plan}</strong></>}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="cloudinary-refresh-btn"
        >
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Verifier maintenant
        </Button>
      </div>

      {alerts.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="cloudinary-alerts">
          {alerts.map(al => (
            <div
              key={al.id}
              className={`p-3 rounded border text-sm flex items-start justify-between gap-3 ${
                al.level === "critical" ? "bg-red-50 border-red-300 text-red-800" : "bg-orange-50 border-orange-300 text-orange-800"
              }`}
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">{al.level === "critical" ? "Critique" : "Attention"}</div>
                  <div>{al.message}</div>
                </div>
              </div>
              <button
                onClick={() => dismissAlert(al.id)}
                className="text-xs underline hover:opacity-70"
                data-testid={`alert-dismiss-${al.id}`}
              >
                Fermer
              </button>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <Skeleton className="h-32" />
      ) : (
        <div className="space-y-4">
          {rows.map(row => (
            <div key={row.key} data-testid={`cloudinary-row-${row.key}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-primary">{row.label}</span>
                <span className="text-muted-foreground">
                  Consommation : {row.formatter(row.usedRaw)}
                  {row.limitRaw
                    ? <> / Limite : {row.formatter(row.limitRaw)}</>
                    : <> / Limite : non exposée par le plan</>}
                  {row.pct != null && <span className="ml-2 font-mono">· {row.pct}%</span>}
                </span>
              </div>
              {row.pct != null ? (
                <div className="mt-1 h-2 w-full bg-gray-100 rounded overflow-hidden">
                  <div
                    className={`h-full transition-all ${barColor(row.pct)}`}
                    style={{ width: `${Math.min(100, row.pct)}%` }}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Product Reviews Management Page
function SiteSettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [cloudinaryStatus, setCloudinaryStatus] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [aiCacheStats, setAiCacheStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewSection, setShowNewSection] = useState(false);
  const [newSection, setNewSection] = useState({ name: "", slug: "", type: "category", parent: "voyages" });
  const [saving, setSaving] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);

  // Pré-lancement (mode Coming Soon)
  const [preLaunchMode, setPreLaunchMode] = useState(true); // fail-safe: true
  const [savingPreLaunch, setSavingPreLaunch] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({ 
    current: "", 
    new: "", 
    confirm: "" 
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  // Password strength validation
  const validatePasswordStrength = (password) => {
    const errors = [];
    if (password.length < 8) errors.push("Au moins 8 caractères");
    if (!/[A-Z]/.test(password)) errors.push("Au moins une majuscule");
    if (!/[a-z]/.test(password)) errors.push("Au moins une minuscule");
    if (!/[0-9]/.test(password)) errors.push("Au moins un chiffre");
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) errors.push("Au moins un caractère spécial (!@#$%...)");
    return errors;
  };

  const getPasswordStrengthColor = (password) => {
    if (!password) return "bg-gray-200";
    const errors = validatePasswordStrength(password);
    if (errors.length === 0) return "bg-green-500";
    if (errors.length <= 2) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getPasswordStrengthText = (password) => {
    if (!password) return "";
    const errors = validatePasswordStrength(password);
    if (errors.length === 0) return "Fort";
    if (errors.length <= 2) return "Moyen";
    return "Faible";
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setPasswordErrors({});
    
    // Validate frontend
    const errors = {};
    
    if (!passwordForm.current) {
      errors.current = "Le mot de passe actuel est requis";
    }
    
    if (!passwordForm.new) {
      errors.new = "Le nouveau mot de passe est requis";
    } else {
      const strengthErrors = validatePasswordStrength(passwordForm.new);
      if (strengthErrors.length > 0) {
        errors.new = strengthErrors.join(", ");
      }
    }
    
    if (!passwordForm.confirm) {
      errors.confirm = "La confirmation est requise";
    } else if (passwordForm.new !== passwordForm.confirm) {
      errors.confirm = "Les mots de passe ne correspondent pas";
    }
    
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await changePassword({
        current_password: passwordForm.current,
        new_password: passwordForm.new,
        confirm_password: passwordForm.confirm
      });
      
      toast.success(response.data.message || "Mot de passe modifié avec succès !");
      
      // Clear the form
      setPasswordForm({ current: "", new: "", confirm: "" });
      setShowPasswords({ current: false, new: false, confirm: false });
      
      // Log out and redirect if server requires re-login
      if (response.data.require_relogin) {
        toast.info("Vous allez être déconnecté pour appliquer les changements...");
        setTimeout(() => {
          logout();
          // Force navigation to login page, bypassing PreLaunchRoute
          navigate('/connexion', { replace: true });
        }, 2000);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors du changement de mot de passe";
      toast.error(message);
      
      // Set field-specific error if identifiable
      if (message.toLowerCase().includes("actuel")) {
        setPasswordErrors({ current: message });
      } else if (message.toLowerCase().includes("correspond")) {
        setPasswordErrors({ confirm: message });
      } else if (message.toLowerCase().includes("différent")) {
        setPasswordErrors({ new: message });
      } else {
        setPasswordErrors({ general: message });
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const loadData = async () => {
    try {
      const [sectionsRes, cloudinaryRes, settingsRes] = await Promise.all([
        getSiteSections({ include_hidden: true }),
        getCloudinaryStatus(),
        getSettings().catch(() => ({ data: {} }))
      ]);
      setSections(sectionsRes.data);
      setCloudinaryStatus(cloudinaryRes.data);
      // Fail-safe: si champ absent, on considère pré-lancement actif (site fermé)
      const currentPreLaunch = settingsRes?.data && Object.prototype.hasOwnProperty.call(settingsRes.data, 'pre_launch_mode')
        ? Boolean(settingsRes.data.pre_launch_mode)
        : true;
      setPreLaunchMode(currentPreLaunch);
      
      // Load AI status separately (may fail if not configured)
      try {
        const { getAIStatus, getAICacheStats } = await import("../lib/api");
        const [aiRes, cacheRes] = await Promise.all([
          getAIStatus(),
          getAICacheStats().catch(() => ({ data: null }))
        ]);
        setAiStatus(aiRes.data);
        setAiCacheStats(cacheRes.data);
      } catch (e) {
        console.error("Could not load AI status:", e);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestAI = async () => {
    setTestingAI(true);
    try {
      const { getAIStatus, getAICacheStats } = await import("../lib/api");
      const [res, cacheRes] = await Promise.all([
        getAIStatus(),
        getAICacheStats().catch(() => ({ data: null }))
      ]);
      setAiStatus(res.data);
      setAiCacheStats(cacheRes.data);
      if (res.data.connection_status === "connected") {
        toast.success("Connexion IA réussie !");
      } else {
        toast.error(`Erreur IA: ${res.data.error || "Connexion échouée"}`);
      }
    } catch (error) {
      toast.error("Impossible de tester la connexion IA");
    } finally {
      setTestingAI(false);
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm("Vider tout le cache IA ? Les prochaines générations seront des appels frais.")) return;
    setClearingCache(true);
    try {
      const { clearAICache, getAICacheStats } = await import("../lib/api");
      await clearAICache();
      const cacheRes = await getAICacheStats();
      setAiCacheStats(cacheRes.data);
      toast.success("Cache IA vidé");
    } catch (error) {
      toast.error("Erreur lors du vidage du cache");
    } finally {
      setClearingCache(false);
    }
  };

  const handleTogglePreLaunch = async (checked) => {
    const previous = preLaunchMode;
    setPreLaunchMode(checked); // optimistic
    setSavingPreLaunch(true);
    try {
      await updateSettings({ pre_launch_mode: checked });
      toast.success(checked
        ? "Mode pré-lancement activé — le site est fermé au public."
        : "Mode pré-lancement désactivé — le site est ouvert au public.");
    } catch (error) {
      setPreLaunchMode(previous); // rollback
      const message = error?.response?.data?.detail || "Erreur lors de la mise à jour";
      toast.error(message);
    } finally {
      setSavingPreLaunch(false);
    }
  };

  const handleToggleVisibility = async (sectionId, visible, additionalOptions = {}) => {
    try {
      const updates = { visible, ...additionalOptions };
      await updateSectionVisibility(sectionId, updates.visible, additionalOptions);
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, ...updates } : s));
      toast.success(`Section mise à jour`);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (!newSection.name.trim()) return;
    
    setSaving(true);
    try {
      const slug = newSection.slug || newSection.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const res = await createSiteSection({ ...newSection, slug });
      setSections(prev => [...prev, res.data]);
      setNewSection({ name: "", slug: "", type: "category", parent: "voyages" });
      setShowNewSection(false);
      toast.success("Catégorie créée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try {
      await deleteSiteSection(sectionId);
      setSections(prev => prev.filter(s => s.id !== sectionId));
      toast.success("Catégorie supprimée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const navSections = sections.filter(s => s.type === "nav");
  const categorySections = sections.filter(s => s.type === "category");

  const defaultSectionIds = ["voyages", "bons-plans", "tests-produits", "association", 
    "croisieres", "hotels", "restaurants", "destinations", "conseils-voyage"];

  return (
    <div data-testid="settings-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-6">
        Paramètres du site
      </h1>

      {loading ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-6">
          {/* Mode pré-lancement (Coming Soon) */}
          <div className="bg-white border border-border rounded-lg p-6" data-testid="pre-launch-mode-section">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <h2 className="font-heading text-lg font-semibold text-primary mb-2">
                  Mode pré-lancement (Coming Soon)
                </h2>
                <p className="text-sm text-muted-foreground">
                  Quand ce mode est activé, seuls les utilisateurs connectés (admin) accèdent au site. Les visiteurs publics voient la page « Coming Soon ». Désactivez-le pour ouvrir le site au public.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  État actuel : <strong className={preLaunchMode ? "text-orange-600" : "text-green-600"}>
                    {preLaunchMode ? "Site fermé (Coming Soon actif)" : "Site ouvert au public"}
                  </strong>
                </p>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  checked={preLaunchMode}
                  onCheckedChange={handleTogglePreLaunch}
                  disabled={savingPreLaunch}
                  data-testid="pre-launch-mode-switch"
                />
              </div>
            </div>
          </div>

          <CloudinaryUsageSection />

          {/* Account Security Section */}
          <div className="bg-white border border-border rounded-lg p-6" data-testid="password-change-section">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-5 w-5 text-accent" />
              <h2 className="font-heading text-lg font-semibold text-primary">Mon compte / Sécurité</h2>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <KeyRound className="h-4 w-4" />
              <span>Connecté en tant que : <strong className="text-primary">{user?.email}</strong></span>
            </div>
            
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <h3 className="font-medium text-sm text-primary">Changer le mot de passe</h3>
              
              {passwordErrors.general && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {passwordErrors.general}
                </div>
              )}
              
              {/* Current Password */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-primary">
                  Mot de passe actuel <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.current}
                    onChange={(e) => {
                      setPasswordForm(prev => ({ ...prev, current: e.target.value }));
                      setPasswordErrors(prev => ({ ...prev, current: null }));
                    }}
                    placeholder="Votre mot de passe actuel"
                    className={passwordErrors.current ? "border-red-500 pr-10" : "pr-10"}
                    data-testid="current-password-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    tabIndex={-1}
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordErrors.current && (
                  <p className="text-xs text-red-500">{passwordErrors.current}</p>
                )}
              </div>
              
              {/* New Password */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-primary">
                  Nouveau mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordForm.new}
                    onChange={(e) => {
                      setPasswordForm(prev => ({ ...prev, new: e.target.value }));
                      setPasswordErrors(prev => ({ ...prev, new: null }));
                    }}
                    placeholder="Nouveau mot de passe"
                    className={passwordErrors.new ? "border-red-500 pr-10" : "pr-10"}
                    data-testid="new-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    tabIndex={-1}
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Password strength indicator */}
                {passwordForm.new && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${getPasswordStrengthColor(passwordForm.new)}`}
                          style={{ width: `${Math.max(20, 100 - validatePasswordStrength(passwordForm.new).length * 20)}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        getPasswordStrengthText(passwordForm.new) === "Fort" ? "text-green-600" :
                        getPasswordStrengthText(passwordForm.new) === "Moyen" ? "text-yellow-600" : "text-red-600"
                      }`}>
                        {getPasswordStrengthText(passwordForm.new)}
                      </span>
                    </div>
                    {validatePasswordStrength(passwordForm.new).length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Manque : {validatePasswordStrength(passwordForm.new).join(", ")}
                      </p>
                    )}
                  </div>
                )}
                
                {passwordErrors.new && (
                  <p className="text-xs text-red-500">{passwordErrors.new}</p>
                )}
              </div>
              
              {/* Confirm Password */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-primary">
                  Confirmer le nouveau mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordForm.confirm}
                    onChange={(e) => {
                      setPasswordForm(prev => ({ ...prev, confirm: e.target.value }));
                      setPasswordErrors(prev => ({ ...prev, confirm: null }));
                    }}
                    placeholder="Confirmer le nouveau mot de passe"
                    className={passwordErrors.confirm ? "border-red-500 pr-10" : "pr-10"}
                    data-testid="confirm-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    tabIndex={-1}
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Match indicator */}
                {passwordForm.confirm && passwordForm.new && (
                  <div className="flex items-center gap-1 text-xs">
                    {passwordForm.new === passwordForm.confirm ? (
                      <>
                        <Check className="h-3 w-3 text-green-500" />
                        <span className="text-green-600">Les mots de passe correspondent</span>
                      </>
                    ) : (
                      <>
                        <X className="h-3 w-3 text-red-500" />
                        <span className="text-red-600">Les mots de passe ne correspondent pas</span>
                      </>
                    )}
                  </div>
                )}
                
                {passwordErrors.confirm && (
                  <p className="text-xs text-red-500">{passwordErrors.confirm}</p>
                )}
              </div>
              
              <div className="pt-2">
                <Button 
                  type="submit" 
                  className="bg-accent hover:bg-accent/90"
                  disabled={changingPassword || !passwordForm.current || !passwordForm.new || !passwordForm.confirm}
                  data-testid="change-password-submit"
                >
                  {changingPassword ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Modification...</>
                  ) : (
                    <><Lock className="h-4 w-4 mr-2" /> Modifier le mot de passe</>
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Après modification, vous serez déconnecté et devrez vous reconnecter avec votre nouveau mot de passe.
              </p>
            </form>
          </div>

          {/* Cloudinary Status */}
          <div className="bg-white border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Cloud className="h-5 w-5 text-accent" />
              <h2 className="font-heading text-lg font-semibold text-primary">Stockage photos (Cloudinary)</h2>
            </div>
            {cloudinaryStatus?.configured ? (
              <div className="flex items-center gap-2 text-green-600">
                <Check className="h-5 w-5" />
                <span>Cloudinary configuré ({cloudinaryStatus.cloud_name})</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                <span>Cloudinary non configuré. Ajoutez les clés API dans le fichier .env</span>
              </div>
            )}
          </div>

          {/* AI Status */}
          <div className="bg-white border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-accent" />
                <h2 className="font-heading text-lg font-semibold text-primary">Génération IA (OpenAI)</h2>
              </div>
              <Button variant="outline" size="sm" onClick={handleTestAI} disabled={testingAI}>
                {testingAI ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Tester</span>
              </Button>
            </div>
            
            {aiStatus ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="font-medium">{aiStatus.provider}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Modèle par défaut:</span>
                  <span className="font-medium">{aiStatus.model}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Clé API:</span>
                  <span className="font-mono text-xs">{aiStatus.api_key_preview || "Non configurée"}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Statut:</span>
                  {aiStatus.connection_status === "connected" ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <Check className="h-4 w-4" /> Connecté
                    </span>
                  ) : aiStatus.connection_status === "error" ? (
                    <span className="flex items-center gap-1 text-red-600">
                      <X className="h-4 w-4" /> Erreur
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600">
                      <AlertCircle className="h-4 w-4" /> Non testé
                    </span>
                  )}
                </div>
                {aiStatus.error && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    <p className="font-medium mb-1">Erreur détectée:</p>
                    <p className="text-xs">{aiStatus.error}</p>
                    {aiStatus.error.includes("quota") && (
                      <p className="mt-2 text-xs">
                        <strong>Solution:</strong> Rechargez votre compte OpenAI ou vérifiez votre plan de facturation.
                      </p>
                    )}
                    {aiStatus.error.includes("invalid_api_key") && (
                      <p className="mt-2 text-xs">
                        <strong>Solution:</strong> Vérifiez que la clé API est correcte dans le fichier backend/.env
                      </p>
                    )}
                  </div>
                )}
                {aiStatus.test_response && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    Test réussi: "{aiStatus.test_response}"
                  </div>
                )}
                
                {/* AI Cache Section */}
                {aiCacheStats && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-muted-foreground" />
                        Cache IA
                        {aiCacheStats.enabled ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Actif</span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Désactivé</span>
                        )}
                      </h4>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleClearCache}
                        disabled={clearingCache || aiCacheStats.total_entries === 0}
                      >
                        {clearingCache ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        <span className="ml-1">Vider</span>
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <p className="text-lg font-semibold text-primary">{aiCacheStats.total_entries}</p>
                        <p className="text-xs text-muted-foreground">Entrées</p>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <p className="text-lg font-semibold text-primary">{aiCacheStats.total_hits}</p>
                        <p className="text-xs text-muted-foreground">Utilisations</p>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <p className="text-lg font-semibold text-primary">{aiCacheStats.ttl_hours}h</p>
                        <p className="text-xs text-muted-foreground">Durée</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      Le cache évite les appels IA identiques. Les résultats expirent après {aiCacheStats.ttl_hours} heures.
                      Lors de la génération, utilisez "Forcer nouvelle génération" pour ignorer le cache.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                <span>Configuration IA non chargée. Cliquez sur "Tester" pour vérifier.</span>
              </div>
            )}
          </div>

          {/* Navigation Sections */}
          <div className="bg-white border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="h-5 w-5 text-accent" />
              <h2 className="font-heading text-lg font-semibold text-primary">Visibilité des rubriques</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Contrôlez où chaque rubrique apparaît sur le site. Utile pour masquer les sections vides ou en préparation.
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium">Rubrique</th>
                    <th className="text-center py-2 font-medium px-2">Menu</th>
                    <th className="text-center py-2 font-medium px-2">Accueil</th>
                    <th className="text-center py-2 font-medium px-2">Footer</th>
                    <th className="text-center py-2 font-medium px-2">Actif</th>
                  </tr>
                </thead>
                <tbody>
                  {navSections.map(section => (
                    <tr key={section.id} className="border-b last:border-0">
                      <td className="py-3">
                        <span className="font-medium">{section.name}</span>
                        {!section.visible && (
                          <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Masqué</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-2">
                        <Switch 
                          checked={section.show_in_menu !== false}
                          onCheckedChange={(checked) => handleToggleVisibility(section.id, section.visible, { show_in_menu: checked })}
                        />
                      </td>
                      <td className="text-center py-3 px-2">
                        <Switch 
                          checked={section.show_on_home !== false}
                          onCheckedChange={(checked) => handleToggleVisibility(section.id, section.visible, { show_on_home: checked })}
                        />
                      </td>
                      <td className="text-center py-3 px-2">
                        <Switch 
                          checked={section.show_in_footer !== false}
                          onCheckedChange={(checked) => handleToggleVisibility(section.id, section.visible, { show_in_footer: checked })}
                        />
                      </td>
                      <td className="text-center py-3 px-2">
                        <Switch 
                          checked={section.visible !== false}
                          onCheckedChange={(checked) => handleToggleVisibility(section.id, checked)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Category Sections */}
          <div className="bg-white border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-accent" />
                <h2 className="font-heading text-lg font-semibold text-primary">Catégories</h2>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowNewSection(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle catégorie
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Gérez les catégories de contenu (Voyages, Tests Produits, etc.)
            </p>

            {/* New Section Form */}
            {showNewSection && (
              <form onSubmit={handleCreateSection} className="mb-4 p-4 border rounded-lg bg-muted/30">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Input
                    placeholder="Nom de la catégorie"
                    value={newSection.name}
                    onChange={(e) => setNewSection(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <Input
                    placeholder="Slug (optionnel)"
                    value={newSection.slug}
                    onChange={(e) => setNewSection(prev => ({ ...prev, slug: e.target.value }))}
                  />
                  <Select 
                    value={newSection.parent} 
                    onValueChange={(v) => setNewSection(prev => ({ ...prev, parent: v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Parent" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voyages">Voyages</SelectItem>
                      <SelectItem value="tests-produits">Tests Produits</SelectItem>
                      <SelectItem value="bons-plans">Bons Plans</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 bg-accent hover:bg-accent/90" disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowNewSection(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </form>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categorySections.map(section => (
                <div key={section.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium block truncate">{section.name}</span>
                    {section.parent && (
                      <span className="text-xs text-muted-foreground">{section.parent}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={section.visible !== false}
                      onCheckedChange={(checked) => handleToggleVisibility(section.id, checked)}
                    />
                    {!defaultSectionIds.includes(section.id) && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleDeleteSection(section.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info about photo sync */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-heading text-lg font-semibold text-blue-800 mb-2">
              Synchronisation des photos
            </h3>
            <p className="text-blue-700 text-sm">
              Pour importer automatiquement vos photos depuis iPhone vers le site, 
              configurez un dossier iCloud ou Dropbox synchronisé. Les photos seront 
              automatiquement importées et disponibles pour la génération d'articles.
            </p>
            <p className="text-blue-600 text-xs mt-2">
              Configuration requise : Cloudinary + dossier cloud synchronisé
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Dashboard Page
export default function DashboardPage() {
  return (
    <DashboardLayout>
      <Routes>
        <Route index element={<OverviewPage />} />
        <Route path="articles" element={<ArticlesPage />} />
        <Route path="categories" element={<CategoriesManagementPage />} />
        <Route path="destinations" element={<DestinationsManagementPage />} />
        <Route path="albums" element={<AlbumsPage />} />
        <Route path="photos" element={<PhotosPage />} />
        <Route path="media/:id" element={<MediaDetailPage />} />
        <Route path="media-import" element={<MediaImportPage />} />
        <Route path="trips" element={<TripsManagementPage />} />
        <Route path="reviews" element={<ProductReviewsAdminPage />} />
        <Route path="comments" element={<CommentsPage />} />
        <Route path="members" element={<MembersPage />} />
        <Route path="membership-applications" element={<MembershipApplicationsPage />} />
        <Route path="adherents/paiements" element={<PaymentsListPage />} />
        <Route path="memberships" element={<MembershipsPage />} />
        <Route path="users" element={<UsersManagementPage />} />
        <Route path="privileges" element={<PrivilegesManagementPage />} />
        <Route path="communications" element={<CommunicationsPage />} />
        <Route path="newsletter" element={<NewsletterSubscribersPage />} />
        <Route path="intelligence" element={<IntelligencePage />} />
        <Route path="deals" element={<DealsManagementPage />} />
        <Route path="banners" element={<BannersManagementPage />} />
        <Route path="partner-requests" element={<PartnerRequestsManagementPage />} />
        <Route path="documents" element={<AdminDocumentsPage />} />
        <Route path="diffusion" element={<DiffusionPage />} />
        <Route path="settings" element={<SiteSettingsPage />} />
        <Route path="parametres/securite" element={<SuperAdminPasskeysPage />} />
        <Route path="voyages-articles" element={<VoyagesEtArticlesPage />} />
        <Route path="medias/lots" element={<MediaImportBatchesPage />} />
        <Route path="medias/lots/:id" element={<MediaBatchDetailPage />} />
        <Route path="medias/corbeille" element={<MediaTrashPage />} />
        <Route path="fiches-pratiques" element={<PracticalSheetsListPage />} />
        <Route path="fiches-pratiques/:id" element={<PracticalSheetEditorPage />} />
        <Route path="system/updates" element={<UpdatesPage />} />
        <Route path="blue-studio" element={<BlueStudioPage embedded />} />
        <Route path="blue-publishing" element={<AdminBluePublishingPage />} />
      </Routes>
    </DashboardLayout>
  );
}
